import React, { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, ArrowRight, ArrowLeft, Star, ExternalLink, Check, Clock, Shield,
  Code, Brain, Target, Zap, Layers, Activity, Search, Settings,
  CreditCard, MessageSquare, LayoutDashboard, Package, LogOut,
  ChevronRight, Github, ArrowUpRight, Send, ShoppingCart, Minus, Plus, Trash2, User,
  ChevronDown, Tag, SlidersHorizontal, Home,
  Sun, Palette, Sparkles, Camera, Wifi, Cpu, Battery, Bell, Music, Sunrise,
  Hourglass, Eye, Image as ImageIcon, Lightbulb, Globe, Lock, Circle, Sliders,
  CircleDot, Smartphone, Volume2
} from "lucide-react";
import "./App.css";
import Orb from "./components/Orb";
import DotGrid from "./components/DotGrid";
import ChromaGrid from "./components/ChromaGrid";
import TextPressure from "./components/TextPressure";
import DecryptedText from "./components/DecryptedText";
import { auth, googleProvider } from "./firebase";
import { signInWithPopup, signInWithRedirect, getRedirectResult, onAuthStateChanged, signOut } from "firebase/auth";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const syncUserWithBackend = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const idToken = await firebaseUser.getIdToken();
      const response = await fetch(`${API_URL}/auth/firebase-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth sync failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Handle redirect result — onAuthStateChanged below will sync user automatically
    getRedirectResult(auth).catch((err) => console.error('Redirect result error:', err));
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      syncUserWithBackend(firebaseUser);
    });
    return () => unsubscribe();
  }, [syncUserWithBackend]);

  const login = (userData) => setUser(userData);
  
  const logout = async () => {
    try {
      await signOut(auth);
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== HOOKS ====================
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options]);

  return [ref, isInView];
};

const useCounter = (end, duration = 2000, inView) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const steps = 60;
    const increment = end / steps;
    const stepTime = duration / steps;

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [end, duration, inView]);

  return count;
};

// ==================== COMPONENTS ====================

// Navigation
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Products', path: '/products' },
    { name: 'Open Source', path: '/open-source' },
    { name: 'Research', path: '/research' },
    { name: 'Platform', path: '/platform' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'AI Consultant', path: '/ai-consultant' }
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen ? 'bg-black/90 backdrop-blur-sm' : 'bg-transparent'
      }`}
    >
      <div className="container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-native-build-2/artifacts/jdfw97wd_logo%202.png" 
              alt="ioteverythin" 
              className="h-8 w-8 object-contain"
            />
            <span className="text-sm font-medium tracking-widest uppercase">ioteverythin</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map(link => (
              <Link 
                key={link.path}
                to={link.path}
                className="text-[#888] hover:text-white transition-colors text-xs tracking-wider uppercase link-underline"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center gap-6">
            {user ? (
              <>
                <Link to="/dashboard" className="text-xs tracking-wider uppercase text-[#888] hover:text-white transition-colors" data-testid="dashboard-btn">
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="text-xs tracking-wider uppercase text-[#888] hover:text-white transition-colors" data-testid="logout-btn">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/shop" className="btn-primary text-xs py-3 px-6" data-testid="shop-btn">
                  Shop
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden pt-6 pb-8 px-2 border-t border-[#1a1a1a] animate-fade-in bg-[#09090a]">
            {navLinks.map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-4 text-[#888] hover:text-white text-sm tracking-wider uppercase"
                onClick={() => setMobileOpen(false)}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                {link.name}
              </Link>
            ))}
            <div className="flex gap-4 mt-6 pt-6 border-t border-[#1a1a1a]">
              {user ? (
                <>
                  <Link to="/dashboard" className="btn-primary flex-1 text-center text-xs" onClick={() => setMobileOpen(false)}>
                    Dashboard
                  </Link>
                  <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="btn-secondary flex-1 text-xs">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/shop" className="btn-primary flex-1 text-center text-xs" onClick={() => setMobileOpen(false)}>
                    Shop
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer
const Footer = () => (
  <footer className="border-t border-[#1a1a1a] py-20 relative z-10" data-testid="footer">
    <div className="container">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-16">
        <div className="col-span-2 md:col-span-1">
          <div className="flex items-center gap-3 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-native-build-2/artifacts/jdfw97wd_logo%202.png" 
              alt="ioteverythin" 
              className="h-8 w-8 object-contain"
            />
            <span className="text-sm tracking-widest uppercase">ioteverythin</span>
          </div>
          <p className="text-[#555] text-sm leading-relaxed">Building AI-native systems across software, intelligence, and hardware.</p>
        </div>
        <div>
          <h4 className="text-xs tracking-wider uppercase text-[#555] mb-6">Products</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/products" className="text-[#888] hover:text-white transition-colors">OpenDocs</Link></li>
            <li><Link to="/products" className="text-[#888] hover:text-white transition-colors">ProphetForge</Link></li>
            <li><Link to="/products" className="text-[#888] hover:text-white transition-colors">PotPal</Link></li>
            <li><Link to="/products" className="text-[#888] hover:text-white transition-colors">Ora</Link></li>
            <li><a href="https://www.vozvox.com/" target="_blank" rel="noopener noreferrer" className="text-[#888] hover:text-white transition-colors">VozVox</a></li>
            <li><Link to="/products/satvoro" className="text-[#888] hover:text-white transition-colors">Satvoro</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-wider uppercase text-[#555] mb-6">Company</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/research" className="text-[#888] hover:text-white transition-colors">Research</Link></li>
            <li><Link to="/open-source" className="text-[#888] hover:text-white transition-colors">Open Source</Link></li>
            <li><Link to="/consultation" className="text-[#888] hover:text-white transition-colors">Consulting</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs tracking-wider uppercase text-[#555] mb-6">Support</h4>
          <ul className="space-y-3 text-sm">
            <li><Link to="/platform" className="text-[#888] hover:text-white transition-colors">Platform</Link></li>
            <li><a href="mailto:support@ioteverythin.com" className="text-[#888] hover:text-white transition-colors">Contact</a></li>
            <li><Link to="/support" className="text-[#888] hover:text-white transition-colors">Support Tickets</Link></li>
            <li><a href="/ioteverythin-portfolio.pdf" target="_blank" rel="noopener noreferrer" className="text-[#888] hover:text-white transition-colors">Portfolio</a></li>
            <li><Link to="/terms" className="text-[#888] hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
          </ul>
        </div>
      </div>
      <div className="divider mb-8" />
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-[#555] tracking-wider text-center sm:text-left" data-testid="footer-legal">
        <p>© 2025 IOTEVERYTHIN (OPC) PRIVATE LIMITED</p>
        <p>Bangalore, Karnataka 560033, India</p>
      </div>
    </div>
  </footer>
);

// ==================== PAGES ====================

// Scroll-driven "orb leads" effect: as the Ethos section reaches viewport center,
// the orb grows forward and a backdrop-blur layer fades in over everything else.
const useOrbFocusScroll = () => {
  // Smooth lerped orb state — updated every frame, no CSS transitions needed
  const orbState = useRef({ x: 0, y: 0, size: 650, opacity: 1, inited: false });

  useEffect(() => {
    let rafId;
    const lerp = (a, b, t) => a + (b - a) * t;

    const tick = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const isMobile = vw < 768;
      const s = orbState.current;

      if (!s.inited) {
        s.x = vw / 2;
        s.y = vh / 2;
        s.size = 650;
        s.opacity = 1;
        s.inited = true;
      }

      // Philosophy section — orb grows at center
      const philEl = document.getElementById('philosophy-section');
      let focus = 0;
      if (philEl) {
        const rect = philEl.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const distance = Math.abs(sectionCenter - vh / 2);
        focus = Math.max(0, 1 - distance / (vh * 0.6));
      }

      // How far past ethos are we? Single smooth ramp 0→1
      let progress = 0;
      if (philEl) {
        const r = philEl.getBoundingClientRect();
        const pastCenter = (vh / 2) - r.bottom;
        if (pastCenter > 0) {
          progress = Math.min(1, pastCenter / (vh * 0.35));
        }
      }

      // Once transitioning away from ethos, kill the focus zoom
      if (progress > 0) focus = focus * (1 - progress);
      document.documentElement.style.setProperty('--orb-focus', focus.toFixed(3));

      // Section targets — tiny indicator dots
      const sections = [
        { id: 'stats-section', size: isMobile ? 80 : 120 },
        { id: 'products-section', size: isMobile ? 60 : 100 },
        { id: 'cta-section', size: isMobile ? 70 : 110 },
      ];

      // Find active section
      let activeTarget = null;
      let minDist = Infinity;
      for (const sec of sections) {
        const el = document.getElementById(sec.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const dist = Math.abs(sectionCenter - vh / 2);
        if (rect.bottom > 0 && rect.top < vh && dist < minDist) {
          minDist = dist;
          activeTarget = { ...sec, rect, sectionCenter };
        }
      }

      // Target position: left margin
      const containerMaxW = vw >= 1400 ? 1400 : 1200;
      const containerLeft = Math.max(24, (vw - containerMaxW) / 2);
      const sideX = isMobile ? 16 : containerLeft * 0.4;

      // Calculate targets
      let targetX, targetY, targetSize, targetOpacity;

      if (progress > 0) {
        // Past ethos: always small, on the side
        const smallSize = activeTarget ? activeTarget.size : (isMobile ? 10 : 14);
        const sectionY = activeTarget ? activeTarget.sectionCenter : s.y;

        targetX = sideX;
        targetY = sectionY;
        targetSize = smallSize;
        targetOpacity = 0.45;
      } else {
        targetX = vw / 2;
        targetY = vh / 2;
        targetSize = 650;
        targetOpacity = 1;
      }

      // Smooth lerp — faster for size (0.12) so it shrinks quickly
      const posSpeed = 0.06;
      const sizeSpeed = 0.1;
      s.x = lerp(s.x, targetX, posSpeed);
      s.y = lerp(s.y, targetY, posSpeed);
      s.size = lerp(s.size, targetSize, sizeSpeed);
      s.opacity = lerp(s.opacity, targetOpacity, 0.08);

      document.documentElement.style.setProperty('--orb-x', `${s.x.toFixed(1)}px`);
      document.documentElement.style.setProperty('--orb-y', `${s.y.toFixed(1)}px`);
      document.documentElement.style.setProperty('--orb-scale', (s.size / 650).toFixed(4));
      document.documentElement.style.setProperty('--orb-side-opacity', s.opacity.toFixed(3));

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);
};

// Fixed orb: starts centered, shrinks and glides to left margin after Ethos.
// All movement is JS-lerped (no CSS transitions) for smooth 60fps animation.
const FixedWheel = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  if (!isHome) return null;
  return (
    <div
      data-testid="fixed-wheel"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        transform: `translate(calc(var(--orb-x, 50vw) - 50%), calc(var(--orb-y, 50vh) - 50%)) scale(calc(var(--orb-scale, 1) * (1 + var(--orb-focus, 0) * 0.85 + var(--orb-intro, 0) * 1.4)))`,
        width: 650,
        height: 650,
        pointerEvents: 'none',
        zIndex: 'var(--orb-z, 20)',
        opacity: 'var(--orb-side-opacity, 1)',
        transition: 'none',
        willChange: 'transform, opacity',
      }}
    >
      <Orb hue={195} hoverIntensity={0.5} rotateOnHover forceHoverState={false} />
    </div>
  );
};

// Small inline orb that sits next to page titles on subpages
const TitleOrb = () => (
  <div
    style={{
      display: 'inline-block',
      width: '1em',
      height: '1em',
      verticalAlign: 'middle',
      marginLeft: '0.3em',
      pointerEvents: 'none',
      position: 'relative',
      top: '-0.05em',
    }}
  >
    <Orb hue={195} hoverIntensity={0.2} rotateOnHover={false} forceHoverState={false} />
  </div>
);

// Backdrop that blurs ONLY the background layers (DotGrid, particles) —
// sits behind main content so text stays crisp.
const OrbBackdrop = () => (
  <div
    aria-hidden="true"
    style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1,
      pointerEvents: 'none',
      background: 'rgba(5,5,5,calc(var(--orb-focus, 0) * 0.45))',
      backdropFilter: 'blur(calc(var(--orb-focus, 0) * 10px))',
      WebkitBackdropFilter: 'blur(calc(var(--orb-focus, 0) * 10px))',
      transition: 'background 0.15s linear',
    }}
  />
);


// Section divider with animated line - more subtle
const AnimatedDivider = ({ inView }) => (
  <div className={`h-px bg-gradient-to-r from-transparent via-[#4a4a4a] to-transparent transition-all duration-1000 ${inView ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`} />
);

// One scroll-linked panel per product. Panel wipes in from the left and
// the text inside translates in, both driven by --panel-progress tied to scroll.
// Also contributes to a global --focus-hide on :root so the orb fades out
// whenever ANY focus panel is visible. Scrolling back reverses everything.
const FocusPanel = ({ area, index, total, onOpen }) => {
  const sectionRef = useRef(null);
  const num = String(index + 1).padStart(2, '0');
  const totalStr = String(total).padStart(2, '0');

  useEffect(() => {
    let rafId;
    const tick = () => {
      const el = sectionRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // Progress driven by section top travelling the viewport.
        const raw = (vh - rect.top) / vh;
        const p = Math.max(0, Math.min(1, raw));
        el.style.setProperty('--panel-progress', p.toFixed(4));

        // Contribute to :root --focus-hide: non-zero whenever the panel
        // overlaps the viewport at all. max() across all panels.
        const overlaps = rect.bottom > 0 && rect.top < vh;
        const visibility = overlaps
          ? Math.max(0, Math.min(1, 1 - Math.abs((rect.top + rect.height / 2) - vh / 2) / (vh * 0.7)))
          : 0;
        const key = `--focus-hide-${index}`;
        document.documentElement.style.setProperty(key, visibility.toFixed(4));

        // Combine all per-panel contributions on :root as --focus-hide.
        let maxHide = 0;
        for (let i = 0; i < total; i++) {
          const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(`--focus-hide-${i}`)) || 0;
          if (v > maxHide) maxHide = v;
        }
        document.documentElement.style.setProperty('--focus-hide', maxHide.toFixed(4));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      document.documentElement.style.setProperty(`--focus-hide-${index}`, '0');
    };
  }, [index, total]);

  return (
    <section
      ref={sectionRef}
      className="relative py-6"
      style={{ zIndex: 25, '--panel-progress': 0 }}
      data-testid={`focus-panel-${area.id}`}
    >
      {/* Full-bleed card: wipes from the absolute left edge of the viewport */}
      <div
        className="relative overflow-hidden"
        style={{
          borderTopRightRadius: '28px',
          borderBottomRightRadius: '28px',
          marginRight: '5vw',
          minHeight: '300px',
        }}
      >
        {/* Background wipe — anchored to viewport left */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background: '#f5f5f0',
            transformOrigin: 'left center',
            transform: 'scaleX(var(--panel-progress))',
            borderTopRightRadius: '28px',
            borderBottomRightRadius: '28px',
          }}
        />
        {/* Subtle inner border revealed with the wipe */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            border: '1px solid rgba(10,10,10,0.08)',
            borderTopRightRadius: '28px',
            borderBottomRightRadius: '28px',
            borderLeft: 'none',
            opacity: 'var(--panel-progress)',
          }}
        />

        <div className="relative h-full py-10 md:py-12 px-8 md:px-16 lg:px-24" style={{ color: '#0a0a0a' }}>
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              {/* Left rail: number + orange marker */}
              <div
                className="lg:col-span-3 flex lg:flex-col gap-6 items-start"
                style={{
                  transform: 'translateX(calc(-60px + var(--panel-progress) * 60px))',
                  opacity: 'var(--panel-progress)',
                }}
              >
                <div
                  className="text-xs tracking-[0.3em] font-mono uppercase"
                  style={{ color: '#ff6a00' }}
                >
                  {num} / {totalStr}
                </div>
                <div
                  className="h-px"
                  style={{
                    width: 'calc(20px + var(--panel-progress) * 80px)',
                    background: '#ff6a00',
                  }}
                />
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#888]">
                  {area.tag || 'Product'}
                </div>
              </div>

              {/* Main content */}
              <div className="lg:col-span-6">
                <h3
                  className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight mb-8 leading-[0.95]"
                  style={{
                    transform: 'translateX(calc(-120px + var(--panel-progress) * 120px))',
                    opacity: 'var(--panel-progress)',
                  }}
                >
                  {area.title}
                </h3>
                <p
                  className="text-base md:text-lg leading-relaxed max-w-xl mb-10"
                  style={{
                    color: '#555',
                    transform: 'translateX(calc(-80px + var(--panel-progress) * 80px))',
                    opacity: 'var(--panel-progress)',
                  }}
                >
                  {area.desc}
                </p>
                <button
                  onClick={() => onOpen(area.id)}
                  className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase px-7 py-4 transition-all hover:gap-5"
                  style={{
                    background: '#0a0a0a',
                    color: '#f5f5f0',
                    borderRadius: '999px',
                    transform: 'translateY(calc(20px - var(--panel-progress) * 20px))',
                    opacity: 'var(--panel-progress)',
                  }}
                >
                  Learn more <ArrowUpRight size={14} />
                </button>
              </div>

              {/* Right: visual placeholder tile */}
              <div
                className="lg:col-span-3 flex lg:justify-end"
                style={{
                  transform: 'translateX(calc(80px - var(--panel-progress) * 80px))',
                  opacity: 'var(--panel-progress)',
                }}
              >
                <div
                  className="w-full aspect-square flex items-center justify-center relative overflow-hidden"
                  style={{
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
                    minHeight: '200px',
                  }}
                >
                  <div
                    className="text-6xl md:text-7xl font-light tracking-tight"
                    style={{ color: '#ff6a00' }}
                  >
                    {num}
                  </div>
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'radial-gradient(circle at 30% 30%, rgba(255,106,0,0.15), transparent 60%)',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
    </section>
  );
};

// Focus section intro (heading only), also scroll-linked.
const FocusIntro = () => {
  const ref = useRef(null);
  useEffect(() => {
    let rafId;
    const tick = () => {
      const el = ref.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const raw = (vh - rect.top) / vh;
        const p = Math.max(0, Math.min(1, raw));
        el.style.setProperty('--panel-progress', p.toFixed(4));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);
  return (
    <section
      ref={ref}
      className="relative py-24"
      style={{ '--panel-progress': 0 }}
    >
      <div className="container">
        <div
          className="max-w-3xl"
          style={{
            transform: 'translateX(calc(-200px + var(--panel-progress) * 200px))',
            opacity: 'var(--panel-progress)',
          }}
        >
          <div className="overline mb-6" style={{ letterSpacing: '0.3em', color: '#ff9500' }}>Our Focus</div>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
            Precision-engineered products.<br />
            <span className="text-[#888]">Real-world impact.</span>
          </h2>
        </div>
      </div>
    </section>
  );
};

// Home Page
const HomePage = () => {
  const [heroRef, heroInView] = useInView();
  const [statsRef, statsInView] = useInView();
  const [productsRef, productsInView] = useInView();
  const [philosophyRef, philosophyInView] = useInView();
  const [ctaRef, ctaInView] = useInView();
  const ctaScrollRef = useRef(null);
  const [ctaProgress, setCtaProgress] = useState(0);
  const navigate = useNavigate();

  // Scroll-driven CTA frost/text progress
  useEffect(() => {
    let rafId;
    const tick = () => {
      const el = ctaScrollRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // Start when section enters bottom, complete when section is centered
        const start = vh;
        const end = vh * 0.3;
        const raw = 1 - (rect.top - end) / (start - end);
        setCtaProgress(Math.max(0, Math.min(1, raw)));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const products = [
    { name: 'OpenDocs', desc: 'Multi-format documentation generator', id: 'opendocs' },
    { name: 'ProphetForge', desc: 'AI trading intelligence platform', id: 'prophetforge' },
    { name: 'PotPal', desc: 'Self-aware AI plant care system', id: 'potpal' },
    { name: 'Ora', desc: 'AI-powered ambient ring lamp', id: 'ora' },
    { name: 'HomeAI', desc: 'AI-powered home automation', id: 'homeai' }
  ];

  const focusAreas = [
    {
      title: 'VozVox',
      desc: 'AI voice agents that sound human. Natural, real-time conversational voice for phone and web, built for support, sales, and automation.',
      id: 'vozvox',
      featured: true,
    },
    {
      title: 'ProphetForge',
      desc: 'AI-native trading intelligence that spots signal in noise, backtested across decades of market data.',
      id: 'prophetforge',
    },
    {
      title: 'Ora',
      desc: 'A sculpted ambient ring lamp with on-device AI Vision that paints your room with the mood of any photo, hour, or season.',
      id: 'ora',
    },
  ];

  const stats = [
    { value: 5, suffix: '+', label: 'Products' },
    { value: 12, suffix: '+', label: 'Open Source' },
    { value: 3, suffix: '', label: 'Research Papers' },
    { value: 10, suffix: '+', label: 'Years Experience' }
  ];

  return (
    <div className="animate-page-enter" data-testid="home-page">
      
      {/* Hero — orb as the heart, text flows in from the sides */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden">
        {/* Mobile hero layout */}
        <div className="flex md:hidden flex-col items-center justify-center h-screen px-6 text-center"
          style={{
            opacity: heroInView ? 1 : 0,
            transform: heroInView ? 'translateY(0)' : 'translateY(40px)',
            transition: 'transform 1.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 1.2s ease',
          }}
        >
          <div className="overline mb-6" style={{ letterSpacing: '0.2em' }}>Welcome</div>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight leading-[1.05]" data-testid="hero-title">
            THE EVOLUTION OF<br /><span className="text-[#888]">INTELLIGENCE</span>
          </h1>
          <button
            onClick={() => {
              const el = document.getElementById('philosophy-section');
              el?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="mt-10 text-xs tracking-widest uppercase text-[#888] hover:text-white transition-colors cursor-pointer bg-transparent border-none inline-flex items-center gap-2"
            data-testid="learn-more-btn-mobile"
          >
            Learn more <ArrowUpRight size={14} />
          </button>
        </div>

        {/* Desktop hero layout */}
        <div className="hidden md:grid h-screen items-center px-6 md:px-12" style={{ gridTemplateColumns: '1fr minmax(0, 720px) 1fr' }}>
          {/* Left column: slides in from far left */}
          <div
            className="text-right pr-8 md:pr-16"
            style={{
              transform: heroInView ? 'translateX(0)' : 'translateX(-200px)',
              opacity: heroInView ? 1 : 0,
              filter: heroInView ? 'blur(0)' : 'blur(8px)',
              transition: 'transform 1.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 1.2s ease, filter 1.2s ease',
            }}
          >
            <div className="overline mb-6" style={{ letterSpacing: '0.2em' }}>Welcome</div>
            <h1
              className="text-3xl sm:text-4xl lg:text-7xl font-light tracking-tight leading-[0.95]"
              data-testid="hero-title"
            >
              THE<br />EVOLUTION<br />OF
            </h1>
          </div>

          {/* Center column reserved for the orb */}
          <div aria-hidden="true" />

          {/* Right column: slides in from far right */}
          <div
            className="text-left pl-8 md:pl-16"
            style={{
              transform: heroInView ? 'translateX(0)' : 'translateX(200px)',
              opacity: heroInView ? 1 : 0,
              filter: heroInView ? 'blur(0)' : 'blur(8px)',
              transition: 'transform 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.2s, opacity 1.2s ease 0.2s, filter 1.2s ease 0.2s',
            }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-7xl font-light tracking-tight leading-[0.95] text-[#888] mb-10 whitespace-nowrap">
              INTELLIGENCE
            </h2>
            <button
              onClick={() => {
                const el = document.getElementById('philosophy-section');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-xs tracking-widest uppercase text-[#888] hover:text-white transition-colors cursor-pointer bg-transparent border-none inline-flex items-center gap-2"
              data-testid="learn-more-btn"
            >
              Learn more <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* Our Ethos — big centered statement */}
      <section id="philosophy-section" ref={philosophyRef} className="py-40">
        <div className="container">
          <div
            className="max-w-4xl mx-auto text-center"
            style={{
              transform: philosophyInView ? 'translateY(0)' : 'translateY(60px)',
              opacity: philosophyInView ? 1 : 0,
              filter: philosophyInView ? 'blur(0)' : 'blur(8px)',
              transition: 'transform 1.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 1.2s ease, filter 1.2s ease',
            }}
          >
            <div className="overline mb-8" style={{ letterSpacing: '0.3em', color: '#ff9500' }}>Our Ethos</div>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] mb-12">
              Precision matters.<br />
              <span className="text-[#888]">Performance wins.</span>
            </h2>
            <p className="text-[#aaa] text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-12">
              Our AI-native product platform is engineered for builders who demand excellence, shifting the odds in favor of speed, precision, and uncompromising technical superiority.
            </p>
            <button onClick={() => navigate('/register')} className="btn-primary" data-testid="ethos-join-btn">
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Stats — alternating slide-ins from left/right */}
      <section id="stats-section" ref={statsRef} className="py-32 relative overflow-hidden">
        <div className="container">
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 24,
              padding: '48px 32px',
            }}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-16">
              {stats.map((stat, i) => {
                const fromLeft = i < 2;
                return (
                  <div
                    key={stat.label}
                    style={{
                      transform: statsInView ? 'translateX(0)' : `translateX(${fromLeft ? -160 : 160}px)`,
                      opacity: statsInView ? 1 : 0,
                      transition: `transform 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.12}s, opacity 1s ease ${i * 0.12}s`,
                    }}
                  >
                    <StatCard stat={stat} inView={statsInView} delay={i * 0.15} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Our Focus — ChromaGrid */}
      <div ref={productsRef} />
      <section id="products-section" className="relative py-32 overflow-hidden">
        <div className="container mb-16 text-center">
          <div className="overline mb-6" style={{ letterSpacing: '0.3em', color: '#ff9500' }}>Our Focus</div>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-light tracking-tight">
            Precision Products
          </h2>
          <p className="mt-8 text-[#888] max-w-xl mx-auto">
            Precision-engineered tools. Real-world impact.
          </p>
        </div>
        <ChromaGrid
          radius={320}
          columns={3}
          items={focusAreas.map((a, i) => ({
            id: a.id,
            title: a.title,
            description: a.desc,
            label: String(i + 1).padStart(2, '0'),
            borderColor: '#ff9500',
            gradient: `linear-gradient(${145 + i * 30}deg, rgba(255, 149, 0, 0.08), #0a0a0b)`,
            featured: a.featured,
          }))}
          onCardClick={(card) => navigate(`/products/${card.id}`)}
        />
      </section>

      {/* CTA */}
      <section id="cta-section" ref={(el) => { ctaRef.current = el; ctaScrollRef.current = el; }} className="py-32 relative overflow-hidden">
        {/* Frost overlay sliding in from left */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '24px',
            transform: `translateX(${-100 + ctaProgress * 100}%)`,
            opacity: ctaProgress,
            transition: 'none',
            zIndex: 1,
          }}
        />
        <div
          className="container relative"
          style={{
            zIndex: 2,
            transform: `translateX(${-60 + ctaProgress * 60}px)`,
            opacity: ctaProgress,
          }}
        >
          <div className="max-w-2xl mx-auto text-center" style={{ transform: `translateX(${(1 - ctaProgress) * -40}px)` }}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-6">
              <DecryptedText
                text="READY TO BUILD?"
                animateOn="view"
                speed={40}
                maxIterations={12}
                sequential={true}
                revealDirection="start"
                className="text-white"
                encryptedClassName="text-[#444]"
              />
            </h2>
            <p className="text-[#888] mb-12 max-w-md mx-auto">
              <DecryptedText
                text="Join builders who demand excellence, precision, and technical superiority."
                animateOn="view"
                speed={30}
                maxIterations={8}
                sequential={true}
                revealDirection="start"
                className="text-[#888]"
                encryptedClassName="text-[#333]"
              />
            </p>
            <div style={{ opacity: Math.max(0, (ctaProgress - 0.5) * 2), transform: `translateY(${(1 - Math.max(0, (ctaProgress - 0.5) * 2)) * 20}px)` }}>
              <button onClick={() => navigate('/consultation')} className="btn-primary" data-testid="cta-consultation-btn">
                Book a Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ stat, inView, delay }) => {
  const count = useCounter(stat.value, 2000, inView);
  return (
    <div
      className="text-center"
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className="flex justify-center mb-2">
        <TextPressure
          text={`${count}${stat.suffix}`}
          fontFamily="Inter"
          fontWeight={300}
          weight={true}
          width={true}
          italic={false}
          fontSize={64}
          textColor="#ffffff"
          stroke={false}
        />
      </div>
      <div className="text-xs tracking-wider uppercase text-[#555]">{stat.label}</div>
    </div>
  );
};

// Products Page
const ProductsPage = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInView();

  const products = [
    { name: 'VozVox', id: 'vozvox', desc: 'AI voice agents that sound human, for phone and web. Natural, real-time conversational voice for support, sales, and automation.', tags: ['Voice', 'AI', 'Agents'], externalUrl: 'https://www.vozvox.com/' },
    { name: 'OpenDocs', id: 'opendocs', desc: 'Multi-format documentation generator that transforms your codebase into beautiful, searchable documentation. Supports Markdown, RST, and custom templates.', tags: ['Documentation', 'AI'], externalUrl: 'https://github.com/ioteverythin/OpenDocs' },
    { name: 'ProphetForge', id: 'prophetforge', desc: 'AI-powered trading intelligence platform for market analysis, pattern recognition, and predictive insights.', tags: ['Trading', 'Analytics'], externalUrl: 'https://www.prophetforge.com/' },
    { name: 'PotPal', id: 'potpal', desc: 'Self-aware AI-driven plant care system that senses, predicts, and adapts watering, lighting, and climate in real time.', tags: ['AI', 'IoT', 'Plant Care'] },
    { name: 'Ora', id: 'ora', desc: 'AI-powered ambient ring lamp. Wall halo or tabletop lamp that adapts color and brightness to your room in real time.', tags: ['Lighting', 'AI Vision', 'IoT'] },
    { name: 'ClearTrace', id: 'traceops', desc: 'Observability platform for distributed systems with powerful visualization and alerting.', tags: ['DevOps', 'Monitoring'], externalUrl: 'https://github.com/ioteverythin/ClearTrace' },
    { name: 'HomeAI', id: 'homeai', desc: 'AI-powered home automation platform that learns your habits and optimizes comfort, energy, and security intelligently.', tags: ['IoT', 'AI', 'Automation'] },
    { name: 'Satvoro', id: 'satvoro', desc: 'Real-time satellite tracking and orbital intelligence. Visualize 68,000+ orbital objects, predict passes, and plan launches with collision avoidance.', tags: ['Space', 'AI', 'Satellite'] }
  ];

  const handleProductClick = (product) => {
    if (product.externalUrl) {
      window.open(product.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/products/${product.id}`);
    }
  };

  return (
    <div className="animate-page-enter pt-32 pb-24 relative" data-testid="products-page">
      <div className="container relative z-10">
        <div ref={ref} className={`mb-20 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="section-label">Products</div>
          <h1 className="font-display text-5xl md:text-7xl">Product Ecosystem<TitleOrb /></h1>
        </div>

        <ChromaGrid
          columns={2}
          radius={300}
          items={products.map((p, i) => ({
            id: p.id,
            title: p.name,
            description: p.desc,
            label: String(i + 1).padStart(2, '0'),
            borderColor: '#ff9500',
            gradient: `linear-gradient(${145 + i * 25}deg, rgba(255, 149, 0, 0.06), #0a0a0b)`,
          }))}
          onCardClick={(card) => handleProductClick(products.find(p => p.id === card.id))}
        />
      </div>
    </div>
  );
};

// ==================== POTPAL LANDING PAGE ====================
const PotPalPage = () => {
  const navigate = useNavigate();

  const pillars = [
    { k: 'Senses', v: '24/7 soil, light & climate' },
    { k: 'Decides', v: 'On-device agentic AI' },
    { k: 'Acts', v: 'Waters, lights & adapts' },
  ];

  const autonomy = [
    { n: '01', t: 'Sense', d: 'Onboard sensors continuously sample soil moisture, temperature, ambient light and air conditions, building a live picture of the plant’s environment.' },
    { n: '02', t: 'Reason', d: 'The on-device AI agent runs our patented plant-care model, combining real-time data with a research-backed digital twin to predict what the plant needs next.' },
    { n: '03', t: 'Act', d: 'PotPal waters precisely, tunes its grow light and adjusts care plans automatically. No taps, no schedules. Just a thriving plant.' },
  ];

  const features = [
    { icon: Activity, t: 'Fully autonomous', d: 'A continuous agentic loop senses, decides, and acts: the right care at the right moment, without your input.' },
    { icon: Shield, t: 'Patented & research-backed', d: 'Not a wrapper around generic rules. Grounded in patented techniques and peer-reviewed research developed in-house.' },
    { icon: Zap, t: 'Offline-first', d: 'The agent runs on-device. No internet? No problem. Your plant keeps thriving even when the cloud takes a break.' },
    { icon: LayoutDashboard, t: 'Optional cloud sync', d: 'Connect PotPal to the app for live readings, photo timelines, and gentle nudges when you’re away.' },
    { icon: Layers, t: 'Modular hardware', d: 'Swap reservoirs, add sensors, upgrade lights. Every PotPal grows with your collection and works with standalone modules.' },
    { icon: MessageSquare, t: 'Quietly intelligent', d: 'No flashing lights, no constant pings. PotPal speaks up only when something genuinely needs you.' },
  ];

  const modules = [
    { t: 'Sensor module', d: 'Soil moisture, EC, temperature, humidity, lux: every reading the agent needs.' },
    { t: 'Water module', d: 'Quiet pump, sealed reservoir, precise micro-doses calibrated to pot volume.' },
    { t: 'Light module', d: 'Tunable full-spectrum LED that adjusts intensity and duration to growth stage.' },
  ];

  const lineup = [
    { tier: 'Solo', img: '/potpal/03-potpal-in-living-room.jpeg', tagline: 'One pot. One plant. Zero thinking.', features: ['1 plant monitored', 'Automated watering', 'Basic AI light schedule', 'Mobile alerts'], cta: 'Notify me' },
    { tier: 'Grid', img: '/potpal/02-potpal-lineup-three-units-in-a-row.jpeg', tagline: 'A self-orchestrated grow setup.', features: ['Up to 25 plants', 'Predictive care intelligence', 'Adaptive grow-light control', 'Energy optimization'], cta: 'Notify me', featured: true },
    { tier: 'Modules', img: '/potpal/04-potpal-pair.jpeg', tagline: 'Add sensor, water and light modules à la carte.', features: ['Sensor module – soil & ambient', 'Water module – reservoir & pump', 'Light module – tunable LED', 'Snap into any PotPal'], cta: 'Notify me' },
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="potpal-page">
      {/* HERO */}
      <section className="container">
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-[#555] hover:text-white mb-12 transition-colors text-sm tracking-wider uppercase"
        >
          <ArrowLeft size={14} /> Back to Products
        </button>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
          <div>
            <div className="flex flex-wrap gap-2 mb-8">
              <span className="badge"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff9500] mr-2 align-middle" />Patented & research-backed</span>
              <span className="badge">Made in Bangalore</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.05] mb-6">
              The plant pot that <span className="text-[#ff9500]">cares for itself.</span>
            </h1>
            <p className="text-[#888] text-lg leading-relaxed max-w-xl mb-10">
              PotPal is a self-aware plant care system. An on-device AI agent senses, reasons, and acts every minute, watering, lighting, and adapting care so your plants thrive without you lifting a finger.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button onClick={() => navigate('/pricing')} className="btn-primary">Join the waitlist <ArrowRight size={14} /></button>
              <a href="#how-it-works" className="btn-secondary">See how it works</a>
            </div>

            <div className="grid grid-cols-3 gap-6 max-w-lg">
              {pillars.map(p => (
                <div key={p.k}>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#555] mb-2">{p.k}</div>
                  <div className="text-sm text-white leading-snug">{p.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Product visual */}
          <div className="relative">
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#0f0f10] via-[#0a0a0b] to-[#0f0f10] border border-[#1a1a1a] relative overflow-hidden" style={{ transform: 'translateZ(0)' }}>
              <div className="absolute inset-0 opacity-30 z-10 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 40%, rgba(255,149,0,0.18), transparent 60%)' }} />
              <img
                src="/potpal/01-hero-product-img.jpeg"
                alt="PotPal smart planter"
                className="absolute -inset-px w-[calc(100%+2px)] h-[calc(100%+2px)] object-cover opacity-90"
              />
              {/* Floating spec chips */}
              <div className="absolute top-6 left-6 z-20 flex items-center gap-2 px-3 py-2 rounded-full bg-[#0a0a0b]/80 backdrop-blur border border-[#1a1a1a]">
                <Activity size={12} className="text-[#ff9500]" />
                <span className="text-[10px] text-[#888]">Soil 38% • Optimal</span>
              </div>
              <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2 px-3 py-2 rounded-full bg-[#0a0a0b]/80 backdrop-blur border border-[#1a1a1a]">
                <Zap size={12} className="text-[#ff9500]" />
                <span className="text-[10px] text-[#888]">Auto-watering</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="container mt-24">
        <div className="border-y border-[#1a1a1a] py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {['Patented care model', 'Research-backed', 'On-device AI', 'Designed in India'].map(t => (
            <div key={t} className="text-xs tracking-[0.15em] uppercase text-[#555]">{t}</div>
          ))}
        </div>
      </section>

      {/* HOW AUTONOMY WORKS */}
      <section id="how-it-works" className="container mt-32">
        <div className="section-label"><span>How autonomy works</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">An AI agent lives inside every PotPal.</h2>
        <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
          Other smart pots send notifications. PotPal closes the loop. It senses, reasons, and physically acts, every minute, on its own, with no app required.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {autonomy.map(s => (
            <div key={s.n} className="card p-8">
              <div className="text-[#ff9500] font-display text-2xl mb-6">{s.n}</div>
              <h3 className="font-display text-2xl mb-3">{s.t}</h3>
              <p className="text-sm text-[#888] leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AMBIENT LINEUP STRIP */}
      <section className="container mt-32">
        <div className="rounded-2xl border border-[#1a1a1a] overflow-hidden relative">
          <img src="/potpal/02-potpal-lineup-three-units-in-a-row.jpeg" alt="PotPal lineup" className="w-full h-[280px] md:h-[420px] object-cover mix-blend-luminosity opacity-85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#ff9500] mb-2">The lineup</div>
            <div className="font-display text-2xl md:text-3xl text-white">Solo • Grid • Modules</div>
          </div>
        </div>
      </section>

      {/* WHY POTPAL FEATURES */}
      <section className="container mt-32">
        <div className="section-label"><span>Why PotPal</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">Set it down. Forget about it. Watch it thrive.</h2>
        <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
          Everything PotPal does, it does without asking. The app is for moments you want a peek, not a permission slip.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className="card p-6">
                <div className="w-10 h-10 rounded-lg border border-[#1a1a1a] flex items-center justify-center mb-5 text-[#ff9500]">
                  <Icon size={18} />
                </div>
                <h3 className="text-base font-medium text-white mb-2">{f.t}</h3>
                <p className="text-sm text-[#888] leading-relaxed">{f.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* MODULES */}
      <section id="modules" className="container mt-32">
        <div className="rounded-2xl border border-[#1a1a1a] bg-gradient-to-br from-[#0d0d0e] to-[#0a0a0b] p-10 md:p-16">
          <div className="section-label"><span>Modular by design</span></div>
          <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">Three modules. One intelligent loop.</h2>
          <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
            Sensor, water and light modules snap together, each one feeding signal into (or taking action from) the on-device agent.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {modules.map((m, i) => (
              <div key={m.t} className="border border-[#1a1a1a] rounded-xl p-6 bg-[#0a0a0b] flex flex-col">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#555] mb-3">Module 0{i + 1}</div>
                <h3 className="font-display text-xl mb-2">{m.t}</h3>
                <p className="text-sm text-[#888] leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-[#555] text-xs tracking-widest uppercase mt-8">
            <span>Sensor</span><ArrowRight size={14} /><span>Agent</span><ArrowRight size={14} /><span>Water + Light</span>
          </div>
        </div>
      </section>

      {/* LINEUP */}
      <section id="lineup" className="container mt-32">
        <div className="section-label"><span>The PotPal lineup</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">One autonomous platform. Three ways to grow.</h2>
        <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
          From a single beloved plant to a fully orchestrated grow rack. Every PotPal is fully autonomous, with the same agentic AI at its core.
        </p>
        <div className="grid md:grid-cols-3 gap-6">
          {lineup.map(l => (
            <div
              key={l.tier}
              className={`card flex flex-col overflow-hidden ${l.featured ? 'border-[#ff9500]/40 shadow-[0_0_0_1px_rgba(255,149,0,0.1)]' : ''}`}
            >
              <div className="relative h-44 overflow-hidden border-b border-[#1a1a1a]">
                <img src={l.img} alt={l.tier} className="w-full h-full object-cover mix-blend-luminosity opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b]/80 to-transparent" />
              </div>
              <div className="p-8 flex flex-col flex-1">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#ff9500] mb-3 h-4">
                {l.featured ? 'Most popular' : ''}
              </div>
              <h3 className="font-display text-3xl mb-2">{l.tier}</h3>
              <p className="text-sm text-[#888] mb-6 leading-relaxed">{l.tagline}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {l.features.map(f => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#ccc]">
                    <Check size={14} className="text-[#ff9500] mt-1 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/pricing')}
                className={l.featured ? 'btn-primary w-full justify-center' : 'btn-secondary w-full justify-center'}
              >
                {l.cta}
              </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPANION APP */}
      <section id="app" className="container mt-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="section-label"><span>Companion app</span></div>
            <h2 className="font-display text-4xl md:text-5xl mb-6">A peek, not a permission slip.</h2>
            <p className="text-[#888] leading-relaxed mb-6">
              The app is optional. Use it for live readings, photo timelines, growth charts and gentle nudges, or don’t. PotPal works either way.
            </p>
            <ul className="space-y-3 mb-8">
              {['Live sensor dashboard', 'Care timeline & photo history', 'Plant identification & profiles', 'Multi-pot orchestration'].map(x => (
                <li key={x} className="flex items-center gap-3 text-sm text-[#ccc]">
                  <Check size={14} className="text-[#ff9500]" /> {x}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://play.google.com/store"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-5 py-3 rounded-lg border border-[#1a1a1a] bg-[#0d0d0e] hover:border-[#ff9500]/40 transition-colors"
              >
                <svg width="22" height="24" viewBox="0 0 24 26" fill="none" aria-hidden>
                  <path d="M3.6 1.2c-.4.4-.6 1-.6 1.7v20.2c0 .7.2 1.3.6 1.7l11.4-11.8L3.6 1.2z" fill="#ff9500"/>
                  <path d="M19.4 9.3l-3.7-2.1-3.5 3.6 3.5 3.6 3.7-2.1c1.4-.8 1.4-2.2 0-3z" fill="#ffb547"/>
                  <path d="M3.6 24.8c.5.5 1.2.6 2 .1l13.8-7.9-3.7-3.7L3.6 24.8z" fill="#ffd28a"/>
                  <path d="M3.6 1.2l12.1 12.1 3.7-3.7L5.6 1.1c-.8-.5-1.5-.4-2 .1z" fill="#ffa733"/>
                </svg>
                <div className="text-left">
                  <div className="text-[9px] text-[#888] tracking-wider">GET IT ON</div>
                  <div className="text-sm text-white font-medium">Google Play</div>
                </div>
              </a>
              <a
                href="https://www.apple.com/app-store/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-5 py-3 rounded-lg border border-[#1a1a1a] bg-[#0d0d0e] hover:border-[#ff9500]/40 transition-colors"
              >
                <svg width="22" height="24" viewBox="0 0 24 26" fill="#ff9500" aria-hidden>
                  <path d="M17.05 13.36c-.03-2.92 2.39-4.32 2.5-4.39-1.36-2-3.49-2.27-4.24-2.3-1.81-.18-3.53 1.06-4.45 1.06-.92 0-2.34-1.04-3.85-1.01-1.98.03-3.81 1.15-4.83 2.92-2.06 3.57-.53 8.85 1.48 11.74.98 1.42 2.15 3.01 3.66 2.96 1.47-.06 2.03-.95 3.81-.95 1.78 0 2.28.95 3.83.92 1.58-.03 2.59-1.45 3.55-2.87 1.12-1.65 1.58-3.25 1.6-3.34-.04-.02-3.06-1.18-3.09-4.68zM14.07 4.85c.81-.99 1.36-2.36 1.21-3.72-1.17.05-2.59.78-3.43 1.76-.75.87-1.41 2.27-1.23 3.61 1.31.1 2.65-.66 3.45-1.65z"/>
                </svg>
                <div className="text-left">
                  <div className="text-[9px] text-[#888] tracking-wider">DOWNLOAD ON THE</div>
                  <div className="text-sm text-white font-medium">App Store</div>
                </div>
              </a>
            </div>
            <p className="text-[10px] text-[#444] mt-3">Apps launching alongside hardware.</p>
          </div>
          <div className="relative h-[520px] sm:h-[560px] flex items-center justify-center overflow-hidden">
            {[
              { src: '/potpal/05-app-receiving-module-data.jpeg', rotate: -8, xPct: -38, z: 1 },
              { src: '/potpal/07-potpal-profile-screen.jpeg', rotate: 8, xPct: 38, z: 2 },
              { src: '/potpal/06-potpal-command-center.jpeg', rotate: 0, xPct: 0, z: 3 },
            ].map((p, i) => (
              <div
                key={i}
                className="absolute w-44 sm:w-52 lg:w-56 h-[380px] sm:h-[440px] lg:h-[480px] rounded-[36px] border border-[#1a1a1a] bg-[#0a0a0b] p-2 shadow-2xl overflow-hidden"
                style={{ transform: `translateX(${p.xPct}%) rotate(${p.rotate}deg)`, zIndex: p.z }}
              >
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-10" />
                <img src={p.src} alt="PotPal app" className="w-full h-full object-cover rounded-[28px]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mt-32">
        <div className="rounded-2xl border border-[#1a1a1a] bg-gradient-to-br from-[#0f0f10] to-[#0a0a0b] p-12 md:p-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(255,149,0,0.12), transparent 70%)' }} />
          <div className="relative">
            <h2 className="font-display text-4xl md:text-6xl mb-6 max-w-3xl mx-auto leading-[1.05]">
              Plants thrive. <span className="text-[#ff9500]">You relax.</span>
            </h2>
            <p className="text-[#888] max-w-xl mx-auto mb-10 leading-relaxed">
              PotPal is launching soon. Join the waitlist for early access pricing and first delivery slots.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={() => navigate('/pricing')} className="btn-primary">Join the waitlist <ArrowRight size={14} /></button>
              <button onClick={() => navigate('/ai-consultant')} className="btn-secondary"><Brain size={14} /> Ask Matt about PotPal</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== ORA PAGE ====================
const OraPage = () => {
  const navigate = useNavigate();
  const [variant, setVariant] = useState('black');

  const pillars = [
    { k: 'Ambient light', v: 'Sculpted halo glow.' },
    { k: 'AI Vision', v: 'Reads photos. Casts mood.' },
    { k: 'Ring + flat', v: 'Wall halo or desk lamp.' },
    { k: 'Time-aware', v: 'Adapts hour by hour.' },
  ];

  const proofPills = ['Ambient light', 'WLED-compatible', 'Works offline', 'Matter-ready', 'On-device AI'];

  const visionSteps = [
    { n: '01', t: 'Capture', d: 'Upload a photo or shoot one in the moment. ORA understands the scene, not just the pixels.' },
    { n: '02', t: 'Extract', d: 'On-device AI identifies the dominant tones, secondary accents, and mood signature, like a color theorist on demand.' },
    { n: '03', t: 'Diffuse', d: 'The palette flows across every segment of your ORA ring, in real time. Your space wears the photo.' },
  ];

  const features = [
    { icon: Brain, t: 'AI Optimizer always on', d: 'A continuous loop senses light, temperature, time of day, and your moods, then nudges color and brightness to match.', span: 'lg:col-span-2' },
    { icon: Clock, t: 'Time-of-day intelligence', d: 'Cool focus light at noon. Warm amber at sunset. Soft red at midnight. ORA shifts on its own, no schedules to set.', span: 'lg:col-span-2' },
    { icon: Wifi, t: 'Offline-first', d: 'Works fully without internet. Your light stays smart even when the cloud takes a break.', span: '' },
    { icon: CircleDot, t: 'Multi-segment ring', d: '144 LEDs per meter, individually addressable. Run gradients, chases, or split it into zones.', span: '' },
    { icon: Shield, t: 'Hardware-grade build', d: 'Anodized aluminum chassis, PMMA diffuser, USB-C PD power. Engineered to outlast trends.', span: '' },
    { icon: Eye, t: 'Vision-driven palettes', d: 'Snap a sunset, a painting, a fabric. ORA reads the photo and paints your room with the same mood. The ambient lighting is generated by AI in real time.', span: 'lg:col-span-3' },
  ];

  const moods = [
    { t: 'Golden Hour', d: 'Warm amber wash for evenings.', glow: 'from-amber-500/30 via-orange-500/20' },
    { t: 'Movie Night', d: 'Deep violet ambient backlight.', glow: 'from-violet-600/30 via-purple-700/20' },
    { t: 'Romantic', d: 'Soft pinks for slow evenings.', glow: 'from-pink-500/30 via-rose-500/20' },
    { t: 'Ocean Calm', d: 'Cool teals for focus and rest.', glow: 'from-teal-500/30 via-cyan-600/20' },
  ];

  const controls = [
    { icon: Sun, t: 'Brightness ring', d: 'Sweep from soft glow to floodlight with a single arc gesture.' },
    { icon: Palette, t: 'Quick colors', d: 'Eight curated swatches always one tap away.' },
    { icon: Zap, t: 'Live effects', d: 'Solid, blink, breathe, wipe, chase. Mix and remix.' },
    { icon: Sliders, t: 'BRI · SPD · INT', d: 'Three sliders. Fine-tune any effect to taste.' },
  ];

  const tools = [
    { icon: Layers, t: 'Segment Visualizer', d: 'View real WLED segments' },
    { icon: Battery, t: 'Battery Indicator', d: 'Phone battery on LEDs' },
    { icon: Sparkles, t: 'Smart Moods', d: 'Time & weather automation' },
    { icon: Sun, t: 'Time of Day', d: 'Day · evening · night' },
    { icon: Hourglass, t: 'LED Timer', d: 'Visual countdown' },
    { icon: Bell, t: 'Notifications', d: 'Flash on alerts' },
    { icon: Music, t: 'Music Sync', d: 'Sync to audio', soon: true },
    { icon: Sunrise, t: 'Wake-up Light', d: 'Sunrise alarm', soon: true },
  ];

  const specs = [
    { icon: Lightbulb, h: 'LEDs', v: '144 / m', d: 'Individually addressable WS2812B' },
    { icon: Cpu, h: 'Compute', v: 'ESP32-S3', d: 'Dual-core, AI on-device' },
    { icon: Wifi, h: 'Connectivity', v: 'Wi-Fi 6', d: '+ BLE 5, Matter-ready' },
    { icon: Activity, h: 'Refresh', v: '800 Hz', d: 'Flicker-free even on camera' },
    { icon: Palette, h: 'Color depth', v: '16M', d: 'True 24-bit RGB' },
    { icon: Shield, h: 'Build', v: 'Anodized', d: 'Aluminum chassis & PMMA diffuser' },
    { icon: Zap, h: 'Power', v: 'USB-C PD', d: 'Up to 30 W, 1.5 m braided' },
    { icon: Lock, h: 'Privacy', v: 'Local-first', d: 'No cloud accounts required' },
  ];

  const galleryImgs = [
    { src: '/ora/03-gal-1.jpg', cls: 'md:col-span-2 md:row-span-2', h: 'h-[420px] md:h-full' },
    { src: '/ora/04-gal-2.jpg', cls: '', h: 'h-[200px]' },
    { src: '/ora/05-gal-3.jpg', cls: '', h: 'h-[200px]' },
    { src: '/ora/06-gal-4.jpg', cls: '', h: 'h-[200px]' },
    { src: '/ora/07-gal-6.jpg', cls: '', h: 'h-[200px]' },
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="ora-page">
      {/* HERO */}
      <section className="container">
        <button
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-[#555] hover:text-white mb-12 transition-colors text-sm tracking-wider uppercase"
        >
          <ArrowLeft size={14} /> Back to Products
        </button>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
          <div>
            <div className="flex flex-wrap gap-2 mb-8">
              <span className="badge"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff9500] mr-2 align-middle animate-pulse" />System Active · ORA AI</span>
              <span className="badge">v2.4 firmware</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.05] mb-6">
              A ring of light <br/>that <span className="text-[#ff9500]">thinks.</span>
            </h1>
            <p className="text-[#888] text-lg leading-relaxed max-w-xl mb-10">
              ORA is a sculpted ambient lamp powered by on-device AI. Mount it on the wall as a glowing halo, or set it on the desk as a tabletop light. Either way, it adapts color, brightness and mood to your room, every hour, every photo, every season.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <button onClick={() => navigate('/pricing')} className="btn-primary">Pre-order ORA <ArrowRight size={14} /></button>
              <a href="#ai-vision" className="btn-secondary">See AI Vision</a>
            </div>

            {/* Variant selector */}
            <div className="flex items-center gap-4 mb-10">
              <button
                onClick={() => setVariant('black')}
                className={`w-10 h-10 rounded-full border-2 transition-all ${variant === 'black' ? 'border-[#ff9500] scale-110' : 'border-[#1a1a1a]'}`}
                style={{ background: 'radial-gradient(circle at 30% 30%, #2a2a2c, #08090C)' }}
                aria-label="Graphite Black"
              />
              <button
                onClick={() => setVariant('white')}
                className={`w-10 h-10 rounded-full border-2 transition-all ${variant === 'white' ? 'border-[#ff9500] scale-110' : 'border-[#1a1a1a]'}`}
                style={{ background: 'radial-gradient(circle at 30% 30%, #f5f5f5, #c5c5c5)' }}
                aria-label="Cloud White"
              />
              <span className="text-xs tracking-[0.2em] uppercase text-[#888]">{variant === 'black' ? 'Graphite Black' : 'Cloud White'}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-lg">
              {pillars.map(p => (
                <div key={p.k}>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#555] mb-2">{p.k}</div>
                  <div className="text-sm text-white leading-snug">{p.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#0f0f10] via-[#0a0a0b] to-[#0f0f10] border border-[#1a1a1a] relative overflow-hidden" style={{ transform: 'translateZ(0)' }}>
              <div className="absolute inset-0 opacity-40 z-10 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(255,149,0,0.25), transparent 65%)' }} />
              <img
                src={variant === 'white' ? '/ora/hero-white.jpg' : '/ora/hero-black.jpg'}
                alt={`ORA ambient ring lamp - ${variant === 'white' ? 'Cloud White' : 'Graphite Black'}`}
                className="absolute -inset-px w-[calc(100%+2px)] h-[calc(100%+2px)] object-cover"
              />
              <div className="absolute top-6 left-6 z-20 flex items-center gap-2 px-3 py-2 rounded-full bg-[#0a0a0b]/80 backdrop-blur border border-[#1a1a1a]">
                <Eye size={12} className="text-[#ff9500]" />
                <span className="text-[10px] text-[#888]">AI Vision active</span>
              </div>
              <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2 px-3 py-2 rounded-full bg-[#0a0a0b]/80 backdrop-blur border border-[#1a1a1a]">
                <Sun size={12} className="text-[#ff9500]" />
                <span className="text-[10px] text-[#888]">Golden Hour · 18:42</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="container mt-24">
        <div className="border-y border-[#1a1a1a] py-6 grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
          {proofPills.map(t => (
            <div key={t} className="text-xs tracking-[0.15em] uppercase text-[#555]">{t}</div>
          ))}
        </div>
      </section>

      {/* DUAL — Wall & Table */}
      <section id="dual" className="container mt-32">
        <div className="section-label"><span>Two forms. One soul.</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">Mount it on the wall.<br/>Stand it on a desk.</h2>
        <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
          Same hardware. Two completely different rooms. Hang ORA flush as a glowing halo, or stand it upright as a sculpted tabletop lamp.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card overflow-hidden flex flex-col">
            <div className="relative h-[340px] overflow-hidden border-b border-[#1a1a1a]">
              <img src={variant === 'white' ? '/ora/wall-white.jpg' : '/ora/wall-black.jpg'} alt="ORA wall mount" className="absolute -inset-px w-[calc(100%+2px)] h-[calc(100%+2px)] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b]/70 to-transparent" />
              <span className="absolute top-4 left-4 badge"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff9500] mr-2 align-middle" />Wall halo</span>
            </div>
            <div className="p-8">
              <h3 className="font-display text-2xl mb-3">Halo, on the wall</h3>
              <p className="text-sm text-[#888] mb-6 leading-relaxed">A flush ring that reads the room and washes the wall behind it with ambient color. Perfect over the sofa, behind the bed, or as a centerpiece in the hallway.</p>
              <div className="flex flex-wrap gap-2">
                {['Discreet bracket', '3 mins to install', 'USB-C cable'].map(b => (
                  <span key={b} className="text-[10px] tracking-wider uppercase text-[#888] px-3 py-1 rounded-full border border-[#1a1a1a]">{b}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="card overflow-hidden flex flex-col">
            <div className="relative h-[340px] overflow-hidden border-b border-[#1a1a1a]">
              <img src={variant === 'white' ? '/ora/table-white.jpg' : '/ora/table-black.jpg'} alt="ORA tabletop" className="absolute -inset-px w-[calc(100%+2px)] h-[calc(100%+2px)] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b]/70 to-transparent" />
              <span className="absolute top-4 left-4 badge"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff9500] mr-2 align-middle" />Tabletop lamp</span>
            </div>
            <div className="p-8">
              <h3 className="font-display text-2xl mb-3">Halo, on the desk</h3>
              <p className="text-sm text-[#888] mb-6 leading-relaxed">Pop ORA into its anodized aluminum stand and it becomes a sculpted desk light. Perfect for studios, reading nooks, and bedside tables.</p>
              <div className="flex flex-wrap gap-2">
                {['No-slip base', 'Integrated stand', 'Tilt-stable'].map(b => (
                  <span key={b} className="text-[10px] tracking-wider uppercase text-[#888] px-3 py-1 rounded-full border border-[#1a1a1a]">{b}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="container mt-32">
        <div className="section-label"><span>Gallery</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-14 max-w-3xl">Every angle is on <span className="text-[#ff9500]">purpose.</span></h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:auto-rows-[200px]">
          {galleryImgs.map((g, i) => (
            <div key={i} className={`relative overflow-hidden rounded-xl border border-[#1a1a1a] ${g.cls}`}>
              <img src={g.src} alt="" className={`w-full ${g.h} object-cover hover:scale-105 transition-transform duration-700`} />
            </div>
          ))}
        </div>
      </section>

      {/* AI VISION */}
      <section id="ai-vision" className="container mt-32">
        <div className="rounded-2xl border border-[#1a1a1a] bg-gradient-to-br from-[#0d0d0e] to-[#0a0a0b] p-10 md:p-16">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
            <div>
              <div className="section-label"><span>AI Vision</span></div>
              <h2 className="font-display text-4xl md:text-5xl mb-4">Snap a photo.<br/><span className="text-[#ff9500]">Get a palette.</span></h2>
              <p className="text-[#888] max-w-xl mb-10 leading-relaxed">
                ORA's most magical trick: feed it a photo, and it paints your room with the same mood. On-device. In seconds.
              </p>
              <div className="space-y-6">
                {visionSteps.map(s => (
                  <div key={s.n} className="flex gap-5">
                    <div className="text-[#ff9500] font-display text-2xl flex-shrink-0 w-12">{s.n}</div>
                    <div>
                      <h4 className="text-base font-medium text-white mb-1">{s.t}</h4>
                      <p className="text-sm text-[#888] leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-[560px] flex items-center justify-center">
              <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(255,149,0,0.4), transparent 60%)' }} />
              <div className="relative w-64 h-[520px] rounded-[36px] border border-[#1a1a1a] bg-[#0a0a0b] p-2 shadow-2xl overflow-hidden">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-10" />
                <img src="/ora/11-phone-screen.jpg" alt="ORA AI Vision" className="w-full h-full object-cover rounded-[28px]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section id="features" className="container mt-32">
        <div className="section-label"><span>Why ORA</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">An ambient computer for <span className="text-[#ff9500]">your light.</span></h2>
        <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
          ORA isn't a smart bulb. It's a ring of intelligence that lives in your room, sensing, adapting, and orchestrating ambient color in real time.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={i} className={`card p-6 ${f.span}`}>
                <div className="w-10 h-10 rounded-lg border border-[#1a1a1a] flex items-center justify-center mb-5 text-[#ff9500]">
                  <Icon size={18} />
                </div>
                <h3 className="text-base font-medium text-white mb-2">{f.t}</h3>
                <p className="text-sm text-[#888] leading-relaxed">{f.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* SHOWCASE — phones */}
      <section className="container mt-32">
        <div className="section-label"><span>Companion app</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">Optional control. <span className="text-[#ff9500]">Effortless interaction.</span></h2>
        <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
          The ORA app gives you a glance at the system, a library of curated moods, and the AI Vision picker, but you'll rarely need it. ORA already knows.
        </p>
        <div className="grid md:grid-cols-3 gap-8 items-center">
          {[
            { src: '/ora/10-ora-smart-home-dashboard.jpg', t: 'System overview', d: "One glance tells you what ORA is doing, why, and what's coming next.", rot: -3 },
            { src: '/ora/08-phone-screen.jpg', t: 'Curated moods', d: 'A growing library of presets, hand-tuned by designers and remixed by AI.', rot: 0 },
            { src: '/ora/09-phone-screen.jpg', t: 'AI Vision', d: 'Pick any photo. ORA paints your room with the same palette in real time.', rot: 3 },
          ].map((p, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <div className="relative w-56 h-[480px] rounded-[36px] border border-[#1a1a1a] bg-[#0a0a0b] p-2 shadow-2xl overflow-hidden mb-6" style={{ transform: `rotate(${p.rot}deg)` }}>
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-10" />
                <img src={p.src} alt={p.t} className="w-full h-full object-cover rounded-[28px]" />
              </div>
              <h3 className="font-display text-xl mb-2">{p.t}</h3>
              <p className="text-sm text-[#888] leading-relaxed max-w-xs">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MOODS */}
      <section id="moods" className="container mt-32">
        <div className="section-label"><span>Moods, ready to wear</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-4 max-w-3xl">A palette for every <span className="text-[#ff9500]">moment.</span></h2>
        <p className="text-[#888] max-w-2xl mb-14 leading-relaxed">
          Hand-crafted ambient presets, plus the AI to invent more on demand.
        </p>
        <div className="grid md:grid-cols-4 gap-4">
          {moods.map(m => (
            <div key={m.t} className="relative rounded-xl border border-[#1a1a1a] overflow-hidden h-56 group cursor-pointer">
              <div className={`absolute inset-0 bg-gradient-to-br ${m.glow} to-transparent`} />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.15),transparent_60%)]" />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/60 to-transparent">
                <h4 className="font-display text-xl mb-1">{m.t}</h4>
                <p className="text-xs text-[#888]">{m.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTROL DEEP DIVE */}
      <section className="container mt-32">
        <div className="grid lg:grid-cols-[1fr_0.9fr] gap-16 items-center">
          <div>
            <div className="section-label"><span>Take the wheel</span></div>
            <h2 className="font-display text-4xl md:text-5xl mb-6">Precise, when you want it.</h2>
            <p className="text-[#888] leading-relaxed mb-10 max-w-xl">
              For the moments you want full creative control: a brightness ring, color picker, speed and intensity sliders, and a library of effects. Solid, blink, breathe, wipe, and more.
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              {controls.map((c, i) => {
                const Icon = c.icon;
                return (
                  <div key={i} className="border border-[#1a1a1a] rounded-xl p-5 bg-[#0d0d0e]">
                    <div className="flex items-center gap-2 mb-2 text-[#ff9500]">
                      <Icon size={16} />
                      <h4 className="text-sm font-medium text-white">{c.t}</h4>
                    </div>
                    <p className="text-xs text-[#888] leading-relaxed">{c.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative h-[560px] flex items-center justify-center">
            <div className="absolute inset-0 opacity-25" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(255,149,0,0.4), transparent 60%)' }} />
            <div className="relative w-64 h-[520px] rounded-[36px] border border-[#1a1a1a] bg-[#0a0a0b] p-2 shadow-2xl overflow-hidden" style={{ transform: 'rotate(-2deg)' }}>
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-10" />
              <img src="/ora/08-phone-screen.jpg" alt="ORA control panel" className="w-full h-full object-cover rounded-[28px]" />
            </div>
          </div>
        </div>
      </section>

      {/* TOOLS */}
      <section id="tools" className="container mt-32">
        <div className="rounded-2xl border border-[#1a1a1a] bg-gradient-to-br from-[#0d0d0e] to-[#0a0a0b] p-10 md:p-16">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
            <div>
              <div className="section-label"><span>Device tools</span></div>
              <h2 className="font-display text-4xl md:text-5xl mb-6">10+ utilities. <br/><span className="text-[#ff9500]">One halo.</span></h2>
              <p className="text-[#888] leading-relaxed">
                ORA doesn't just do ambient. It doubles as your phone's battery indicator, a wake-up sunrise alarm, an LED timer, music sync, and more. The same hardware, dozens of utilities.
              </p>
              <div className="mt-8 rounded-xl overflow-hidden border border-[#1a1a1a]">
                <img src="/ora/12-tools.jpg" alt="ORA tools" className="w-full h-56 object-cover" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {tools.map((t, i) => {
                const Icon = t.icon;
                return (
                  <div key={i} className="flex items-center gap-3 p-4 rounded-lg bg-white/[0.02] border border-[#1a1a1a] hover:border-[#2a2a2a] transition-colors">
                    <div className="w-9 h-9 rounded-md bg-[#ff9500]/10 text-[#ff9500] flex items-center justify-center flex-shrink-0">
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white">{t.t}</div>
                      <div className="text-xs text-[#666]">{t.d}</div>
                    </div>
                    {t.soon && (
                      <span className="text-[9px] font-semibold uppercase tracking-wider text-[#ff9500] bg-[#ff9500]/10 border border-[#ff9500]/20 px-2 py-0.5 rounded-full">Soon</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section id="specs" className="container mt-32">
        <div className="section-label"><span>Specs</span></div>
        <h2 className="font-display text-4xl md:text-5xl mb-14 max-w-3xl">Beautifully <span className="text-[#ff9500]">over-engineered.</span></h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {specs.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="card p-6 text-center">
                <div className="w-12 h-12 rounded-lg bg-[#ff9500]/8 border border-[#ff9500]/20 text-[#ff9500] flex items-center justify-center mx-auto mb-4">
                  <Icon size={20} />
                </div>
                <h4 className="text-[11px] font-semibold tracking-[0.1em] uppercase text-[#555] mb-2">{s.h}</h4>
                <div className="font-display text-2xl mb-2">{s.v}</div>
                <p className="text-xs text-[#888] leading-relaxed">{s.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="container mt-32">
        <div className="rounded-2xl border border-[#1a1a1a] bg-gradient-to-br from-[#0f0f10] to-[#0a0a0b] p-12 md:p-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-50" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(255,149,0,0.18), transparent 70%)' }} />
          <div className="relative">
            <span className="badge mb-6 inline-flex"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff9500] mr-2 align-middle animate-pulse" />Now shipping in India</span>
            <h2 className="font-display text-4xl md:text-6xl mb-6 max-w-3xl mx-auto leading-[1.05]">
              Light that <span className="text-[#ff9500]">thinks for itself.</span>
            </h2>
            <p className="text-[#888] max-w-xl mx-auto mb-10 leading-relaxed">
              Bring ORA home. Mount it on the wall, stand it on a desk. Either way, your space is about to feel right, every hour, every season.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button onClick={() => navigate('/pricing')} className="btn-primary">Pre-order ORA <ArrowRight size={14} /></button>
              <button onClick={() => navigate('/ai-consultant')} className="btn-secondary"><Brain size={14} /> Talk to sales</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Product Detail Page
const ProductDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const externalProducts = {
    'opendocs': 'https://github.com/ioteverythin/OpenDocs',
    'prophetforge': 'https://www.prophetforge.com/',
    'traceops': 'https://github.com/ioteverythin/ClearTrace',
    'vozvox': 'https://www.vozvox.com/'
  };

  const products = {
    'potpal': {
      name: 'PotPal',
      tagline: 'Your plants, intelligently cared for.',
      desc: 'PotPal is a self-aware, AI-driven plant care system designed to monitor, understand, and respond to a plant\'s needs in real time. It combines sensors, automation, and intelligent decision-making to create a fully adaptive growing environment for healthier plants with minimal effort.',
      tags: ['AI', 'IoT', 'Plant Care', 'Automation'],
      features: [
        { title: 'Continuous Plant Sensing', desc: 'Tracks soil moisture, light intensity, temperature, humidity, and ambient conditions continuously in real time.' },
        { title: 'Agentic Autonomous Mode', desc: 'Fully autonomous mode where PotPal independently senses, decides, and acts on watering, lighting, and environmental balance.' },
        { title: 'Semi-Autonomous Mode', desc: 'Set user preferences, thresholds, or schedules while PotPal handles execution and fine-tuning automatically.' },
        { title: 'Predictive Care Intelligence', desc: 'Learns each plant\'s behavior over time, predicts needs before stress appears, and adapts care strategies continuously.' },
        { title: 'AI Grow Light Control', desc: 'Dynamically adjusts light intensity and duration based on plant type, growth stage, and live environmental inputs.' },
        { title: 'Home AI Integration', desc: 'Coordinates with broader Home AI ecosystems and smart devices for holistic indoor environmental control.' }
      ],
      techStack: ['Environmental Sensors', 'Edge AI Models', 'Automated Irrigation Control', 'Smart Lighting Control', 'React', 'FastAPI'],
      useCases: ['Home plant care', 'Urban gardening', 'Office green spaces', 'Hobbyist growers', 'Advanced smart-home users']
    },
    'ora': {
      name: 'Ora',
      tagline: 'A ring of light that thinks.',
      desc: 'Ora is a sculpted ambient lamp powered by on-device AI. Mount it on the wall as a glowing halo or set it on the desk as a tabletop light. It adapts color, brightness and mood to your room, every hour, every photo, every season.',
      tags: ['Voice', 'AI', 'Framework', 'Open Source'],
      features: [
        { title: 'Natural Language Understanding', desc: 'Intent classification, entity extraction, and sentiment analysis powered by transformer models.' },
        { title: 'Custom Wake Words', desc: 'Train a wake word model on your brand name with as few as 50 audio samples. Runs on-device.' },
        { title: 'Multi-Turn Dialogue', desc: 'Context-aware conversation management with slot filling, confirmations, and graceful fallbacks.' },
        { title: 'Speech Synthesis', desc: 'Neural TTS with voice cloning support. Create a unique voice for your brand or use 50+ built-in voices.' },
        { title: 'Multi-Language Support', desc: '40+ languages with automatic language detection and seamless code-switching.' },
        { title: 'Edge Deployment', desc: 'Run Ora on Raspberry Pi, Jetson Nano, or any ARM device. No cloud dependency for core features.' }
      ],
      techStack: ['Python SDK', 'WebSocket Streaming', 'ONNX Runtime', 'gRPC', 'Whisper ASR', 'Custom TTS Engine'],
      useCases: ['Smart home devices', 'Customer service bots', 'Automotive assistants', 'Accessibility tools', 'Interactive kiosks']
    },
    'homeai': {
      name: 'HomeAI',
      tagline: 'Your home, intelligently automated.',
      desc: 'HomeAI is an AI-powered home automation platform that learns your daily routines and adapts lighting, climate, security, and energy usage in real time. No rigid schedules. Just an intelligent home that anticipates your needs.',
      tags: ['IoT', 'AI', 'Automation', 'Smart Home'],
      features: [
        { title: 'Adaptive Routines', desc: 'Learns your habits over time and automatically adjusts lighting, temperature, and appliances without manual schedules.' },
        { title: 'Energy Optimization', desc: 'AI-driven energy management that reduces your bills by optimizing HVAC, lighting, and appliance usage based on occupancy and weather.' },
        { title: 'Smart Security', desc: 'Intelligent threat detection with camera analytics, anomaly alerts, and automated lock/unlock based on recognized faces.' },
        { title: 'Voice & Gesture Control', desc: 'Control your home with natural voice commands or gesture recognition, works offline with on-device processing.' },
        { title: 'Multi-Protocol Hub', desc: 'Connects Zigbee, Z-Wave, Matter, Wi-Fi, and Bluetooth devices into one unified interface.' },
        { title: 'Scene Intelligence', desc: 'Context-aware scenes that adapt based on time, weather, occupancy, and your calendar: movie night, focus mode, wake-up, and more.' }
      ],
      techStack: ['Edge AI', 'Matter/Thread Protocol', 'TensorFlow Lite', 'MQTT', 'React Dashboard', 'Raspberry Pi Compatible'],
      useCases: ['Smart homes', 'Energy management', 'Elderly care automation', 'Rental property management', 'Home security']
    },
    'satvoro': {
      name: 'Satvoro',
      tagline: 'Real-time intelligence for the orbiting world.',
      desc: 'Satvoro is a real-time satellite tracking and orbital analysis platform that monitors and visualizes space objects. It brings live visibility into satellite operations, orbital mechanics, and space traffic management for mission planning, launch coordination, and collision avoidance.',
      tags: ['Space', 'AI', 'Satellite', 'Analytics'],
      comingSoon: true,
      features: [
        { title: 'Live Orbital Tracking', desc: 'Tracks 68,000+ orbital objects in real time with multi-constellation visualization.' },
        { title: 'Pass Prediction', desc: 'Earth-observation satellite pass prediction and collection planning for mission scheduling.' },
        { title: 'Launch Planning', desc: 'Launch coordination with conjunction screening and collision-avoidance assessment.' },
        { title: 'Inter-Satellite Routing', desc: 'Models inter-satellite links and communication paths across constellations.' },
        { title: 'Satvoro AI', desc: 'AI-powered orbital intelligence for space traffic management and decision support.' },
        { title: '3D Visualization', desc: 'Interactive 3D orbital-element visualization with geographic filtering and coverage analysis.' }
      ],
      techStack: ['Orbital Mechanics', 'TLE / SGP4 Propagation', 'WebGL 3D', 'AI Analytics', 'React', 'FastAPI'],
      useCases: ['Mission planning', 'Launch coordination', 'Collision avoidance', 'Earth observation', 'Space traffic management']
    }
  };

  const id = location.pathname.split('/').pop();

  // PotPal has its own bespoke landing page
  if (id === 'potpal') {
    return <PotPalPage />;
  }
  // Ora has its own bespoke landing page
  if (id === 'ora') {
    return <OraPage />;
  }

  // Redirect external products
  if (externalProducts[id]) {
    window.location.href = externalProducts[id];
    return (
      <div className="animate-page-enter min-h-screen flex items-center justify-center pt-20">
        <p className="text-[#888] text-sm">Redirecting...</p>
      </div>
    );
  }

  const product = products[id];
  if (!product) {
    return (
      <div className="animate-page-enter min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="font-display text-3xl mb-4">Product not found</h1>
          <button onClick={() => navigate('/products')} className="btn-primary">Back to Products</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="product-detail-page">
      <div className="container max-w-4xl">
        <button 
          onClick={() => navigate('/products')} 
          className="flex items-center gap-2 text-[#555] hover:text-white mb-12 transition-colors text-sm tracking-wider uppercase"
          data-testid="back-to-products-btn"
        >
          <ArrowLeft size={14} /> Back to Products
        </button>

        {/* Hero */}
        <div className="mb-16">
          {product.comingSoon && (
            <span className="badge mb-4 inline-flex"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff9500] mr-2 align-middle" />Coming Soon</span>
          )}
          <h1 className="font-display text-5xl md:text-7xl mb-4">{product.name}</h1>
          <p className="text-xl text-[#888] mb-2 font-display">{product.tagline}</p>
          <p className="text-[#666] leading-relaxed mb-8 max-w-2xl">{product.desc}</p>
          <div className="flex flex-wrap gap-3">
            {product.tags.map(tag => (
              <span key={tag} className="badge">{tag}</span>
            ))}
          </div>
        </div>

        <div className="divider mb-16" />

        {/* Features */}
        <div className="mb-16">
          <h2 className="text-xs tracking-wider uppercase text-[#555] mb-10">Core Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {product.features.map((feature, i) => (
              <div key={i} className="card p-6">
                <div className="flex items-start gap-4">
                  <span className="text-xs text-[#555] mt-1 flex-shrink-0">0{i + 1}</span>
                  <div>
                    <h3 className="text-sm font-medium text-white mb-2">{feature.title}</h3>
                    <p className="text-xs text-[#888] leading-relaxed">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="divider mb-16" />

        {/* Tech Stack */}
        <div className="mb-16">
          <h2 className="text-xs tracking-wider uppercase text-[#555] mb-8">Tech Stack</h2>
          <div className="flex flex-wrap gap-3">
            {product.techStack.map(tech => (
              <span key={tech} className="px-4 py-2 border border-[#1a1a1a] text-sm text-[#888] rounded">{tech}</span>
            ))}
          </div>
        </div>

        <div className="divider mb-16" />

        {/* Use Cases */}
        <div className="mb-16">
          <h2 className="text-xs tracking-wider uppercase text-[#555] mb-8">Use Cases</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {product.useCases.map(uc => (
              <div key={uc} className="card p-4 text-center">
                <span className="text-sm text-[#888]">{uc}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="divider mb-12" />

        {/* CTA */}
        <div className="text-center">
          <h2 className="font-display text-3xl mb-6">Interested in {product.name}?</h2>
          <div className="flex justify-center gap-4">
            <button onClick={() => navigate('/consultation')} className="btn-primary" data-testid="subscribe-btn">Get in Touch</button>
            <button onClick={() => navigate('/ai-consultant')} className="btn-secondary" data-testid="ai-consult-btn">
              <Brain size={14} /> Ask AI Consultant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Open Source Page
const OpenSourcePage = () => {
  const [ref, inView] = useInView();
  const navigate = useNavigate();

  const repos = [
    { name: 'OpenDocs', desc: 'Documentation generator for modern codebases', tags: ['Python', 'AI'], stars: 142, trending: true, url: 'https://github.com/ioteverythin/OpenDocs' },
    { name: 'ClearTrace', desc: 'Observability toolkit for microservices', tags: ['Go', 'DevOps'], stars: 89, trending: true, url: 'https://github.com/ioteverythin/ClearTrace' },
    { name: 'agentmemorydb', desc: 'Persistent memory layer for AI agents', tags: ['Python', 'AI'], stars: 12, new: true, url: 'https://github.com/ioteverythin/agentmemorydb' },
    { name: 'statefold', desc: 'Framework-agnostic, event-sourced state platform for AI agents. Durable resume, time-travel, replay and branching under LangGraph, CrewAI, Agno or MCP.', tags: ['Python', 'AI', 'Agents'], stars: 3, new: true, url: 'https://github.com/ioteverythin/statefold' },
    { name: 'roxi-tts-v3.1', desc: 'RoxiTTS v3.1 — our latest lightweight expressive text-to-speech model.', tags: ['TTS', 'AI', 'Hugging Face'], label: '0.1B', url: 'https://huggingface.co/IOTEverythin/roxi-tts-v3.1' },
    { name: 'roxi-tts-pro', desc: 'RoxiTTS Pro: 3B Indian-English speech synthesis for voice agents and customer support, built on MOSS-TTS.', tags: ['TTS', 'AI', 'Hugging Face'], label: '3B', url: 'https://huggingface.co/IOTEverythin/roxi-tts-pro' },
    { name: 'roxi-tts-v3', desc: 'RoxiTTS v3 — expressive neural text-to-speech.', tags: ['TTS', 'AI', 'Hugging Face'], label: '0.1B', url: 'https://huggingface.co/IOTEverythin/roxi-tts-v3' },
    { name: 'roxi-tts-v2', desc: 'RoxiTTS v2 — expressive neural TTS, our most-downloaded voice model.', tags: ['TTS', 'AI', 'Hugging Face'], label: '0.1B', url: 'https://huggingface.co/IOTEverythin/roxi-tts-v2' },
    { name: 'roxi-tts-v2-onnx', desc: 'RoxiTTS v2 exported to ONNX for fast, portable cross-platform inference.', tags: ['TTS', 'ONNX', 'Hugging Face'], label: 'ONNX', url: 'https://huggingface.co/IOTEverythin/roxi-tts-v2-onnx' },
    { name: 'roxi-tts-emotion', desc: 'RoxiTTS Emotion: controllable, emotion-conditioned expressive Indian-English speech.', tags: ['TTS', 'AI', 'Hugging Face'], label: '3B', url: 'https://huggingface.co/IOTEverythin/roxi-tts-emotion' },
    { name: 'voxi-tts', desc: 'Expressive British-English text-to-speech model with 8 selectable emotional styles. Fine-tuned Kokoro-82M / StyleTTS2.', tags: ['TTS', 'AI', 'Hugging Face'], label: '0.1B', url: 'https://huggingface.co/IOTEverythin/voxi-tts' },
    { name: 'roxi-duplex', desc: 'Roxi Duplex: full-duplex real-time speech-to-speech dialogue model with barge-in, tuned for Indian English. Built on Moshi.', tags: ['Speech', 'AI', 'Hugging Face'], label: 'S2S', url: 'https://huggingface.co/IOTEverythin/roxi-duplex' },
    { name: 'phoneme-wake-word', desc: 'Phoneme-level CTC wake word and keyword spotting model for ESP32 and TinyML edge devices.', tags: ['Edge AI', 'Speech', 'Hugging Face'], label: 'TinyML', url: 'https://huggingface.co/IOTEverythin/phoneme-wake-word' },
    { name: 'grounded-pointer-qa', desc: 'Grounded extractive QA with abstention. Points to answer spans in context and refuses when unsupported, for hallucination-free RAG.', tags: ['NLP', 'RAG', 'Hugging Face'], label: 'RoBERTa', url: 'https://huggingface.co/IOTEverythin/grounded-pointer-qa' },
    { name: 'cue-v5', desc: 'Cue v5: a compact causal conversation-control model for voice agents. Predicts seven conversational events plus an end-of-turn probability every 160 ms, then maps them to explicit STOP, PAUSE and RESPOND actions. Trained on synthetic calls plus real AMI and ICSI meetings.', tags: ['Speech', 'Turn-taking', 'Research'], label: 'Preprint', url: '/research/cue-v5' }
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="opensource-page">
      <div className="container max-w-4xl">
        <div ref={ref} className={`mb-20 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="section-label">Open Source</div>
          <h1 className="font-display text-5xl md:text-7xl">Open Source<TitleOrb /></h1>
        </div>

        <ChromaGrid
          columns={2}
          radius={300}
          items={repos.map((repo, i) => ({
            id: repo.name,
            title: `ioteverythin/${repo.name}`,
            description: repo.desc,
            label: repo.label || `★ ${repo.stars}`,
            borderColor: '#ff9500',
            gradient: `linear-gradient(${160 + i * 20}deg, rgba(255, 149, 0, 0.06), #0a0a0b)`,
          }))}
          onCardClick={(card) => {
            const repo = repos.find(r => r.name === card.id);
            if (!repo?.url) return;
            if (repo.url.startsWith('/')) navigate(repo.url);
            else window.open(repo.url, '_blank', 'noopener,noreferrer');
          }}
        />
      </div>
    </div>
  );
};

// Research Page
const ResearchPage = () => {
  const [ref, inView] = useInView();
  const navigate = useNavigate();

  const papers = [
    { title: 'Cue: A Conversation Reflex Model for Barge-In and End-of-Turn Control in Cascaded Voice Agents', status: 'Preprint', year: 2026, citations: 0, summary: 'A compact causal controller that predicts seven conversational events plus an end-of-turn probability, then maps them to explicit STOP, PAUSE and RESPOND actions. Real AMI and ICSI meeting supervision raises held-out interruption recall from 0.24 to 0.61 on AMI and 0.37 to 0.63 on ICSI, while low-latency end-of-turn prediction on natural speech remains open.', url: '/research/cue-v5' },
    { title: 'Roxi-Duplex: Low-Resource Indian-English Adaptation of a Full-Duplex Speech-to-Speech Model', status: 'Preprint', year: 2026, citations: 0, summary: 'Adapting a full-duplex speech-to-speech model to Indian English using synthetic two-channel dialogue data, so a voice agent can listen while it speaks and handle interruption, overlap and backchannel.', url: 'https://doi.org/10.5281/zenodo.21445239' },
    { title: 'QR-PINN: Quantile Regression Physics-Informed Neural Networks', status: 'Published', year: 2024, citations: 12, summary: 'A novel approach combining quantile regression with physics-informed neural networks for uncertainty quantification.' },
    { title: 'Score-Decide Decoupling for LLM Patent Classification', status: 'In Review', year: 2025, citations: 0, summary: 'A two-stage framework that separates scoring and decision-making in large language model-based patent classification.' },
    { title: 'Systems Thinking in Competitive Intelligence', status: 'Published', year: 2024, citations: 5, summary: 'Applying systems dynamics and feedback loops to model competitive landscapes and predict market shifts.' }
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24 relative" data-testid="research-page">
      <div className="container max-w-3xl relative z-10">
        <div ref={ref} className={`mb-20 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="section-label">Research</div>
          <h1 className="font-display text-5xl md:text-7xl">Research<TitleOrb /></h1>
        </div>

        <ChromaGrid
          columns={3}
          radius={300}
          items={papers.map((paper, i) => ({
            id: paper.title,
            title: paper.title,
            description: paper.summary,
            label: `${paper.status} · ${paper.year}`,
            borderColor: paper.status === 'In Review' ? '#888' : '#ff9500',
            gradient: `linear-gradient(${150 + i * 30}deg, rgba(255, 149, 0, 0.06), #0a0a0b)`,
          }))}
          onCardClick={(card) => {
            const paper = papers.find(p => p.title === card.id);
            if (!paper?.url) return;
            if (paper.url.startsWith('/')) navigate(paper.url);
            else window.open(paper.url, '_blank', 'noopener,noreferrer');
          }}
        />
      </div>
    </div>
  );
};

// Horizontal bar group for the Cue paper page.
const CueBars = ({ title, note, groups, series, max = 1, fmt = (v) => v.toFixed(2), unit = '' }) => (
  <figure className="m-0 bg-[#0d0d0e] border border-[#1a1a1a] rounded-lg p-4">
    <figcaption className="text-sm font-medium text-white mb-1">{title}</figcaption>
    {note && <p className="text-[#666] text-xs mb-4">{note}</p>}
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.label}>
          <div className="text-xs text-[#888] mb-1.5 font-mono">{g.label}</div>
          <div className="space-y-1.5">
            {series.map((ser) => {
              const v = g.values[ser.key];
              if (v == null) return null;
              return (
                <div key={ser.key} className="flex items-center gap-2">
                  <span className="w-20 shrink-0 text-[10px] text-[#777] font-mono">{ser.name}</span>
                  <div className="flex-1 h-5 bg-[#141415] rounded-sm overflow-hidden">
                    <div
                      className="h-full rounded-sm"
                      style={{ width: `${Math.max(1.5, (v / max) * 100)}%`, background: ser.color }}
                    />
                  </div>
                  <span className="w-24 shrink-0 text-[11px] font-mono text-right" style={{ color: ser.color }}>
                    {fmt(v)}{unit}{g.annot && g.annot[ser.key] ? ` · ${g.annot[ser.key]}` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  </figure>
);

// Cue paper page (v5 preprint).
const CueV5Page = () => {
  const navigate = useNavigate();

  const cell = 'px-3 py-2.5 font-mono text-xs whitespace-nowrap';
  const head = 'px-3 py-2.5 text-[11px] tracking-wider uppercase text-[#666] font-normal text-left whitespace-nowrap';

  const taxonomy = [
    { cls: 'BACKCHANNEL', ev: 'MMM-hmm, okay, right — while another speaker holds the floor', act: 'Continue' },
    { cls: 'ACCIDENTAL', ev: 'cough, laugh, keyboard, background speech, false start', act: 'Continue' },
    { cls: 'SOFT_INTERRUPT', ev: 'wait, one second, hold on', act: 'Pause' },
    { cls: 'HARD_INTERRUPT', ev: 'correction, cancellation, explicit objection', act: 'Stop and listen' },
    { cls: 'TAKEOVER', ev: 'target speaker begins a substantial floor-taking turn', act: 'Stop and listen' },
    { cls: 'USER_TURN', ev: 'target-speaker speech outside a barge-in context', act: '—' },
    { cls: 'NONE', ev: 'silence or line noise', act: '—' },
  ];

  const profiles = [
    { name: 'Responsive', barge: '(0.70, 4)', eot: '(0.50, 6)' },
    { name: 'Balanced (default)', barge: '(0.80, 6)', eot: '(0.90, 12)', bold: true },
    { name: 'Cautious', barge: '(0.95, 6)', eot: '(0.95, 16)' },
  ];

  const sources = [
    { src: 'Synthetic calls', tracks: '2,664', hours: '44.7', notes: '800 barge-in + 500 EOT calls, each clean and realistic; 32 calls re-voiced with Dia callers' },
    { src: 'AMI meetings', tracks: '80', hours: '45.0', notes: '20 meetings from ES2002, ES2005, ES2006, IS1000, TS3005 (a–d), every speaker' },
    { src: 'ICSI meetings', tracks: '30', hours: '34.8', notes: 'Bmr005–009, Bro003–005, Bed002–003; three most talkative speakers per meeting' },
    { src: 'Total', tracks: '2,774', hours: '124.5', notes: 'sampled 50% synthetic / 30% AMI / 20% ICSI', bold: true },
  ];

  const meetingRows = [
    { task: 'AMI interruption', v4: '0.243 [0.211, 0.274], FPR 0.081', v5: '0.614 [0.585, 0.638], FPR 0.086' },
    { task: 'ICSI interruption', v4: '0.374 [0.294, 0.444], FPR 0.099', v5: '0.631 [0.574, 0.703], FPR 0.085' },
    { task: 'AMI EOT', v4: 'no qualifying setting', v5: '0.269 [0.224, 0.316], FPR 0.087, 1.20 s' },
    { task: 'ICSI EOT', v4: 'no qualifying setting', v5: '0.594 [0.471, 0.694], FPR 0.085, 1.06 s' },
  ];

  const turnbench = [
    { name: 'Cue v4', eot: '0.772 / 0.077 / 1.41 s', int: '0.614 / 0.088 / 583 ms' },
    { name: 'Cue v5', eot: '0.800 / 0.070 / 1.16 s', int: '0.804 / 0.079 / 547 ms', ours: true },
    { name: 'VAP', eot: '0.841 / 0.045 / 463 ms', int: '0.957 / 0.100 / 896 ms' },
    { name: 'Kyutai semantic VAD', eot: '0.803 / 0.100 / 1.02 s', int: '0.934 / 0.085 / 584 ms' },
    { name: 'Mimi endpointer', eot: '0.759 / 0.047 / 742 ms', int: '0.876 / 0.100 / 816 ms' },
    { name: 'Smart Turn v3', eot: '0.754 / 0.100 / 1.01 s', int: '0.118 / 0.096 / 151 ms' },
  ];

  const bargeRows = [
    { m: 'v4 clean', hard: '85.5%', take: '90.6%', soft: '93.8%', bc: '3.5%', noise: '5.6%', p50: '243 ms' },
    { m: 'v5 clean', hard: '90.3%', take: '93.8%', soft: '92.5%', bc: '7.0%', noise: '7.4%', p50: '241 ms', ours: true },
    { m: 'v4 realistic', hard: '75.8%', take: '75.0%', soft: '91.2%', bc: '3.5%', noise: '8.0%', p50: '311 ms' },
    { m: 'v5 realistic', hard: '85.5%', take: '81.2%', soft: '93.8%', bc: '4.4%', noise: '11.7%', p50: '281 ms', ours: true },
  ];

  const eotRows = [
    { m: 'v4 clean', mid: '5.2%', ends: '95.5%', p50: '177 ms' },
    { m: 'v5 clean', mid: '4.5%', ends: '95.6%', p50: '137 ms', ours: true },
    { m: 'v4 realistic', mid: '7.5%', ends: '94.4%', p50: '185 ms' },
    { m: 'v5 realistic', mid: '7.5%', ends: '94.1%', p50: '154 ms', ours: true },
  ];

  const ablation = [
    { input: 'Caller only', eotTimer: '0.779', eotLearned: '0.605', int: '0.804' },
    { input: 'Caller + other-speaker audio', eotTimer: '0.800', eotLearned: '0.686', int: '0.712' },
  ];

  const spec = [
    { k: 'Encoder', v: 'One shared frozen Whisper-small encoder (MIT) applied independently to the caller stream and, when available, the agent stream. Each pass produces 768-dimensional features every 20 ms from the two-second window ending at the current 160 ms step, with Whisper’s position table truncated to 100 positions. The frozen encoder holds 87,078,912 parameters.' },
    { k: 'Head', v: 'A six-layer causal Transformer, d = 384, four attention heads, no absolute position embedding in the v5 forward path. Input per frame concatenates caller features, agent features and the one-bit agent-speaking state, for 1,537 values. Each layer attends 1.6 s back, about 9.6 s through the stack. 11,241,224 trainable parameters participate in inference.' },
    { k: 'Outputs', v: 'Separate output layers predict the seven event classes and a scalar end-of-turn probability p(done), both every 20 ms.' },
    { k: 'Policy', v: 'While another speaker is active, the hard-stop score is P(HARD_INTERRUPT) + P(TAKEOVER); STOP fires when it holds above a threshold for consecutive frames, and PAUSE fires analogously from P(SOFT_INTERRUPT), with a 1.5 s cooldown. RESPOND fires after at least 200 ms of caller speech once p(done) holds above its threshold through consecutive frames of silence, with a 2 s fallback.' },
    { k: 'Causality', v: 'Whisper features are recomputed over the two seconds ending at each 160 ms step, so the encoder never receives audio beyond the end of the current step. Every decision is timestamped at the end of the step, which folds all within-step lookahead into reported latency.' },
    { k: 'Leakage control', v: 'Three safeguards. In synthetic calls the agent stream and speaking flag are hidden in the decision window, including up to 1.5 s after each caller turn. Meeting tracks are trained from the target-speaker side only. Agent input is zeroed on 15% of synthetic segments so the controller cannot require the second stream.' },
  ];

  const limitations = [
    'Meetings are not phone calls. AMI and ICSI add real overlap, hesitation and floor-taking, but their acoustics and interaction styles differ from customer-service telephony.',
    'Production evidence remains minimal: three calls, 25 labelled caller turns, no barge-ins.',
    'Synthetic test data influence model and policy selection, so those results are optimistic rather than unbiased final-test estimates.',
    'TurnBench results are development-set results under deterministic four-fold cross-validated setting selection. Hidden-test scoring is needed for leaderboard claims.',
    'TurnBench EOT is not a pure learned-endpoint result. The 0.800 figure uses other-speaker audio and a 960 ms silence rule; the learned p(done) head alone reaches 0.686 recall at 0.081 FPR.',
    'End of turn on real speech remains weak: 0.27 recall on AMI and 0.59 on ICSI under the 10% FPR budget, with roughly one-second delay.',
    'Domain and language coverage remain narrow. Synthetic data emphasize Indian English, and real-meeting supervision contains no SOFT_INTERRUPT labels.',
    'ICSI is not speaker-independent: 14 of 16 test-meeting participants also appear in ICSI training meetings, so AMI is the stronger generalization result.',
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="cue-v5-page">
      <div className="container max-w-3xl">
        <button
          onClick={() => navigate('/research')}
          className="inline-flex items-center gap-2 text-xs tracking-wider uppercase text-[#666] hover:text-white transition-colors mb-10"
          data-testid="cue-back"
        >
          <ArrowLeft size={14} /> Research
        </button>

        <div className="section-label">IOTEverythin Research</div>
        <h1 className="font-display text-3xl md:text-4xl mb-6 leading-tight">
          Cue: A Conversation Reflex Model for Barge-In and End-of-Turn Control in Cascaded Voice Agents<TitleOrb />
        </h1>

        <p className="text-[#888] text-sm mb-6 font-mono">
          Joshua Nishanth Tarun A &middot; Joel Ajitesh Varun A
        </p>

        <p className="text-[#aaa] text-base leading-relaxed mb-5">
          Cascaded voice agents typically use a voice-activity detector to decide when synthesized speech should yield,
          and a silence timer or endpointing model to decide when a user turn is complete. Those mechanisms do not
          distinguish a backchannel from an interruption, or a thinking pause from a completed turn. Cue is a compact
          causal conversation-control model that predicts seven frame-level conversational events together with an
          end-of-turn probability, then maps them to explicit STOP, PAUSE and RESPOND actions.
        </p>
        <p className="text-[#aaa] text-base leading-relaxed mb-8">
          Cue v5 extends the earlier audio-only controller with optional features from the agent&apos;s own audio while
          preserving a leakage-aware training protocol. Its training mixture is roughly 125 hours of caller-side audio
          from synthetic Indian-English recruiter calls and real AMI and ICSI meetings.
        </p>

        <div className="flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs text-[#888] pb-8 border-b border-[#1a1a1a] mb-14">
          <span>Organization <span className="text-white">IOTEverythin</span></span>
          <span>Preprint <span className="text-white">v1.0 candidate, October 2026</span></span>
          <span>Planned release <span className="text-white">Apache-2.0</span></span>
        </div>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">The synthetic-to-real gap</h2>
          <p className="text-[#aaa] text-sm leading-relaxed mb-6">
            The central motivation for v5 is the failure of synthetic-only training on natural meetings. Applied to
            held-out AMI speech, v4&apos;s default endpoint answers inside about 60% of mid-turn pauses, against about
            6% on the synthetic test. Even its most cautious setting remains at 37%. A threshold sweep cannot recover
            the synthetic behavior, so real-conversation supervision is a change in training distribution rather than a
            calibration problem.
          </p>
          <CueBars
            title="Cue v4 answers inside mid-turn pauses"
            note="Responses inside labelled mid-turn pauses. Lower is better. Diagnostic rather than like-for-like: the synthetic and meeting pause definitions differ."
            max={70}
            unit="%"
            fmt={(v) => String(v)}
            groups={[
              { label: 'Synthetic clean (default)', values: { v: 6 } },
              { label: 'AMI held-out (default)', values: { v: 60 } },
              { label: 'AMI held-out (cautious)', values: { v: 37 } },
            ]}
            series={[{ key: 'v', name: 'Cue v4', color: '#c2611a' }]}
          />
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">Held-out real meetings</h2>
          <p className="text-[#aaa] text-sm leading-relaxed mb-6">
            With real AMI and ICSI supervision, interruption recall rises from 0.243 to 0.614 on held-out AMI and from
            0.374 to 0.631 on held-out ICSI, at a false-positive rate of at most 0.10. End of turn improves from having
            no qualifying v4 operating point at all to 0.269 recall on AMI and 0.594 on ICSI. AMI is fully
            speaker-disjoint and gives the stronger evidence; ICSI intervals resample tracks rather than meetings and
            are therefore optimistic.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <CueBars
              title="Interruption recall at FPR 0.10"
              groups={[
                { label: 'AMI', values: { v4: 0.243, v5: 0.614 } },
                { label: 'ICSI', values: { v4: 0.374, v5: 0.631 } },
              ]}
              series={[
                { key: 'v4', name: 'Cue v4', color: '#6b7280' },
                { key: 'v5', name: 'Cue v5', color: '#ff9500' },
              ]}
              fmt={(v) => v.toFixed(3)}
            />
            <CueBars
              title="End-of-turn recall at FPR 0.10"
              note="Cue v4 had no operating point below the 10% FPR budget on either corpus."
              groups={[
                { label: 'AMI', values: { v5: 0.269 }, annot: { v5: '1.20 s' } },
                { label: 'ICSI', values: { v5: 0.594 }, annot: { v5: '1.06 s' } },
              ]}
              series={[{ key: 'v5', name: 'Cue v5', color: '#ff9500' }]}
              fmt={(v) => v.toFixed(3)}
            />
          </div>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>Task</th>
                  <th className={head}>Cue v4</th>
                  <th className={head}>Cue v5</th>
                </tr>
              </thead>
              <tbody>
                {meetingRows.map((r) => (
                  <tr key={r.task} className="border-b border-[#141414] last:border-0">
                    <td className={`${cell} font-sans text-white`}>{r.task}</td>
                    <td className={`${cell} text-[#888]`}>{r.v4}</td>
                    <td className={`${cell} text-[#ff9500]`}>{r.v5}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[#666] text-xs leading-relaxed mt-4">
            Recall with bootstrap 95% intervals and the selected-point FPR, over 2,000 resamples. AMI resamples complete
            meetings; ICSI resamples speaker tracks and is therefore optimistic.
          </p>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">TurnBench, zero-shot</h2>
          <p className="text-[#aaa] text-sm leading-relaxed mb-6">
            No TurnBench training audio is used for either Cue model. Operating points come from fixed four-fold
            cross-validation over the 38 public development conversations. v5 improves both tasks over the re-evaluated
            v4: end of turn from 0.772 to 0.800 recall, interruption from 0.614 to 0.804. VAP remains stronger in recall
            on both tasks. At their separately selected operating points v5 interruption has 547 ms median delay against
            584 ms for Kyutai and 896 ms for VAP, though these are not latency-matched comparisons.
          </p>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>System</th>
                  <th className={`${head} border-l border-[#1a1a1a]`}>End of turn</th>
                  <th className={`${head} border-l border-[#1a1a1a]`}>Interruption</th>
                </tr>
              </thead>
              <tbody>
                {turnbench.map((r) => (
                  <tr key={r.name} className={`border-b border-[#141414] last:border-0 ${r.ours ? 'bg-[#ff9500]/[0.04]' : ''}`}>
                    <td className={`${cell} font-sans ${r.ours ? 'text-white' : 'text-[#bbb]'}`}>{r.name}</td>
                    <td className={`${cell} border-l border-[#1a1a1a] ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.eot}</td>
                    <td className={`${cell} border-l border-[#1a1a1a] ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.int}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[#666] text-xs leading-relaxed mt-4">
            Each cell is recall / FPR / median delay, under the benchmark&apos;s 10% FPR budget. Development-set
            measurements; hidden-test scoring by the maintainers is required for leaderboard claims.
          </p>

          <div className="mt-6 border border-[#ff9500]/20 bg-[#ff9500]/[0.03] rounded-lg p-5">
            <p className="text-[#ccc] text-sm leading-relaxed">
              <span className="text-white font-medium">The 0.800 end-of-turn figure is not learned-endpoint
              performance.</span>{' '}
              The strongest cross-validated configuration combines Cue speech detection, other-speaker audio and an
              explicit 960 ms silence rule. The learned p(done) head alone reaches 0.686 recall at 0.081 FPR with 908 ms
              median delay.
            </p>
          </div>

          <h3 className="font-display text-lg text-white mt-8 mb-3">Input ablation</h3>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>Input</th>
                  <th className={head}>EOT timer</th>
                  <th className={head}>Learned EOT</th>
                  <th className={head}>Interruption</th>
                </tr>
              </thead>
              <tbody>
                {ablation.map((r) => (
                  <tr key={r.input} className="border-b border-[#141414] last:border-0">
                    <td className={`${cell} font-sans text-[#bbb]`}>{r.input}</td>
                    <td className={`${cell} text-[#ccc]`}>{r.eotTimer}</td>
                    <td className={`${cell} text-[#ccc]`}>{r.eotLearned}</td>
                    <td className={`${cell} text-[#ccc]`}>{r.int}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[#666] text-xs leading-relaxed mt-4">
            Other-speaker audio is task-dependent: it improves both end-of-turn rules but reduces interruption recall
            from 0.804 to 0.712. The reported EOT timer uses it; the reported interruption result is caller-only.
          </p>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">Synthetic benchmark</h2>
          <p className="text-[#aaa] text-sm leading-relaxed mb-6">
            On clean synthetic calls, v5 raises hard-interrupt recall from 85.5% to 90.3% and takeover recall from 90.6%
            to 93.8%, while backchannel false stops rise from 3.5% to 7.0%. The gain carries a real false-action
            trade-off, and bootstrap intervals over these sets overlap substantially, so the differences are descriptive
            operating-point changes rather than statistically resolved improvements.
          </p>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg mb-4">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>Model / set</th>
                  <th className={head}>Hard</th>
                  <th className={head}>Takeover</th>
                  <th className={head}>Soft</th>
                  <th className={head}>Backch. stop</th>
                  <th className={head}>Noise stop</th>
                  <th className={head}>Reaction p50</th>
                </tr>
              </thead>
              <tbody>
                {bargeRows.map((r) => (
                  <tr key={r.m} className={`border-b border-[#141414] last:border-0 ${r.ours ? 'bg-[#ff9500]/[0.04]' : ''}`}>
                    <td className={`${cell} font-sans ${r.ours ? 'text-white' : 'text-[#bbb]'}`}>{r.m}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.hard}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.take}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.soft}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.bc}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.noise}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.p50}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[#666] text-xs leading-relaxed mb-8">
            Policies are selected to stop on at most 8% of backchannels on both the clean and realistic sets. Because
            these test sets also participate in checkpoint selection, the numbers are optimistic.
          </p>

          <h3 className="font-display text-lg text-white mb-3">End of turn</h3>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>Model / set</th>
                  <th className={head}>Mid-thought answered</th>
                  <th className={head}>Turn ends answered</th>
                  <th className={head}>Delay p50</th>
                </tr>
              </thead>
              <tbody>
                {eotRows.map((r) => (
                  <tr key={r.m} className={`border-b border-[#141414] last:border-0 ${r.ours ? 'bg-[#ff9500]/[0.04]' : ''}`}>
                    <td className={`${cell} font-sans ${r.ours ? 'text-white' : 'text-[#bbb]'}`}>{r.m}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.mid}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.ends}</td>
                    <td className={`${cell} ${r.ours ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.p50}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[#666] text-xs leading-relaxed mt-4">
            For scale, a 500 ms silence timer answers during 95.5% of labelled mid-thought pauses, and Smart Turn v3.2
            answers during 43.3% while closing 67.7% of true turns inside the scoring window.
          </p>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">Event taxonomy</h2>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>Class</th>
                  <th className={head}>Typical event</th>
                  <th className={head}>Intended action</th>
                </tr>
              </thead>
              <tbody>
                {taxonomy.map((t) => (
                  <tr key={t.cls} className="border-b border-[#141414] last:border-0">
                    <td className={`${cell} text-[#ff9500]`}>{t.cls}</td>
                    <td className="px-3 py-2.5 text-xs text-[#aaa] font-sans">{t.ev}</td>
                    <td className={`${cell} text-[#ccc]`}>{t.act}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">Training data</h2>
          <p className="text-[#aaa] text-sm leading-relaxed mb-6">
            About 124.5 hours of caller-side audio. The raw-hour mixture is not the optimization mixture: synthetic
            calls are 35.9% of raw audio but 50% of sampled training segments, which preserves their dense
            semantic-event labels while still exposing the controller to natural overlap, hesitation and floor
            competition.
          </p>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>Source</th>
                  <th className={head}>Tracks</th>
                  <th className={head}>Hours</th>
                  <th className={head}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((r) => (
                  <tr key={r.src} className={`border-b border-[#141414] last:border-0 ${r.bold ? 'bg-[#ff9500]/[0.04]' : ''}`}>
                    <td className={`${cell} font-sans ${r.bold ? 'text-white' : 'text-[#bbb]'}`}>{r.src}</td>
                    <td className={`${cell} ${r.bold ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.tracks}</td>
                    <td className={`${cell} ${r.bold ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.hours}</td>
                    <td className="px-3 py-2.5 text-xs text-[#888] font-sans">{r.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-6">System</h2>
          <div className="border border-[#1a1a1a] rounded-lg p-6 mb-8 font-mono text-[11px] leading-relaxed text-[#888] overflow-x-auto">
            <div className="whitespace-nowrap">Caller audio (16 kHz) &nbsp;+&nbsp; Agent audio (optional)</div>
            <div className="text-[#444] my-1">|</div>
            <div className="whitespace-nowrap">Frozen Whisper-small &mdash; shared encoder, 2 s windows</div>
            <div className="text-[#444] my-1">|</div>
            <div className="whitespace-nowrap">
              <span className="text-[#ff9500]">Cue v5 causal head</span> &mdash; 6 layers, d = 384, + speaking flag
            </div>
            <div className="text-[#444] my-1">|</div>
            <div className="whitespace-nowrap">7 event classes &nbsp;&nbsp;+&nbsp;&nbsp; end-of-turn probability</div>
            <div className="text-[#444] my-1">|</div>
            <div className="whitespace-nowrap">
              <span className="text-[#ff9500]">Policy</span> &rarr; STOP / PAUSE / RESPOND
            </div>
          </div>
          <dl className="space-y-5 mb-8">
            {spec.map((s) => (
              <div key={s.k} className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-x-6 gap-y-1.5">
                <dt className="font-mono text-xs text-[#666] pt-0.5">{s.k}</dt>
                <dd className="text-[#aaa] text-sm leading-relaxed">{s.v}</dd>
              </div>
            ))}
          </dl>

          <h3 className="font-display text-lg text-white mb-3">Deployment profiles</h3>
          <div className="overflow-x-auto border border-[#1a1a1a] rounded-lg">
            <table className="w-full text-left">
              <thead className="border-b border-[#1a1a1a]">
                <tr>
                  <th className={head}>Profile</th>
                  <th className={head}>Barge-in (threshold, frames)</th>
                  <th className={head}>EOT (threshold, frames)</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((r) => (
                  <tr key={r.name} className={`border-b border-[#141414] last:border-0 ${r.bold ? 'bg-[#ff9500]/[0.04]' : ''}`}>
                    <td className={`${cell} font-sans ${r.bold ? 'text-white' : 'text-[#bbb]'}`}>{r.name}</td>
                    <td className={`${cell} ${r.bold ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.barge}</td>
                    <td className={`${cell} ${r.bold ? 'text-[#ff9500]' : 'text-[#ccc]'}`}>{r.eot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">Cue Tiny</h2>
          <p className="text-[#aaa] text-sm leading-relaxed">
            A 6.2M-parameter strictly causal student distilled from v4. It replaces Whisper with causal log-mel features
            and a lightweight convolutional front-end, batches decisions every 40 ms, and uses roughly 10% of one CPU
            core per call on the tested laptop. Across the synthetic barge-in conditions it stops on 4.7% of
            backchannels and 7.5% of accidental or noise events, detects 84% of hard interruptions and 68% of takeovers,
            with 288 ms median reaction. Endpointing is weaker, answering during 11.2% of clean mid-thought pauses.
            Because Tiny is distilled from v4 rather than v5, treat it as a CPU deployment baseline.
          </p>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">Limitations</h2>
          <ul className="space-y-3">
            {limitations.map((l) => (
              <li key={l} className="text-[#aaa] text-sm leading-relaxed flex gap-3">
                <span className="text-[#ff9500] shrink-0">&bull;</span>
                <span>{l}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-14">
          <h2 className="font-display text-2xl text-white mb-4">Conclusion</h2>
          <p className="text-[#aaa] text-sm leading-relaxed mb-4">
            The most important result is not a single benchmark number but the measured synthetic-to-real gap. A
            synthetic-only model that catches 24% of AMI interruptions and answers into 60% of real mid-turn pauses is
            not evidence of deployment readiness. Adding AMI and ICSI training raises held-out interruption recall to
            61&ndash;63% at a false-positive rate of at most 0.10 and substantially improves zero-shot TurnBench
            performance, while retaining strong synthetic behavior.
          </p>
          <p className="text-[#aaa] text-sm leading-relaxed">
            The remaining limitation is equally clear. End-of-turn prediction on natural speech still requires
            substantial waiting and misses many turns. Cue therefore supports its strongest claim as a leakage-aware,
            compact semantic interruption and conversation-control framework for cascaded voice agents, with general
            low-latency endpointing still open.
          </p>
        </section>

        <p className="text-[#666] text-xs leading-relaxed pt-8 border-t border-[#1a1a1a]">
          Cue is developed by IOTEverythin. Planned public release names are IOTEverythin/cue-v5, cue-v4 and cue-tiny on
          Hugging Face, with runtime code under github.com/ioteverythin/cue and the cue-turn Python package including
          Pipecat integration. AMI and ICSI corpora are used under CC BY 4.0; Whisper code and weights are MIT-licensed;
          TurnBench is used for evaluation only and is not redistributed.
        </p>
      </div>
    </div>
  );
};

// Platform Page
const PlatformPage = () => {
  const [ref, inView] = useInView();
  const navigate = useNavigate();

  const platformFeatures = [
    { title: 'Dashboards', desc: 'Real-time metrics: 1.2M API calls, 98.7% accuracy, 45ms latency. Monitor everything from a single pane.', id: 'dashboards' },
    { title: 'Patent Classifier', desc: 'AI-driven patent classification with score-decide decoupling for precise categorization at scale.', id: 'patent-classifier' },
    { title: 'Document Analyzer', desc: 'Extract structured insights from any document format: PDFs, patents, contracts, and technical papers.', id: 'doc-analyzer' },
    { title: 'Voice Agent Builder', desc: 'Build production-grade voice agents with sub-200ms latency and real-time tool use.', id: 'voice-builder' },
    { title: 'Data Pipeline Designer', desc: 'Visual pipeline builder for ETL, data transformation, and real-time streaming workflows.', id: 'pipeline-designer' },
    { title: 'Intelligence Search', desc: 'Semantic search across patents, technologies, and market data. Machine Learning, Blockchain, Quantum Computing and more.', id: 'intelligence' },
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24 relative" data-testid="platform-page">
      <div className="container max-w-5xl relative z-10">
        <div ref={ref} className={`mb-16 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="section-label">Platform</div>
          <h1 className="font-display text-5xl md:text-7xl mb-4">The Platform<TitleOrb /></h1>
          <p className="text-[#888]">Your AI command center</p>
        </div>

        <ChromaGrid
          columns={3}
          radius={300}
          items={platformFeatures.map((f, i) => ({
            id: f.id,
            title: f.title,
            description: f.desc,
            label: String(i + 1).padStart(2, '0'),
            borderColor: '#ff9500',
            gradient: `linear-gradient(${140 + i * 20}deg, rgba(255, 149, 0, 0.06), #0a0a0b)`,
          }))}
        />

        <div className="text-center mt-12">
          <button onClick={() => navigate('/register')} className="btn-primary" data-testid="access-platform-btn">
            Access the Platform
          </button>
        </div>
      </div>
    </div>
  );
};

// Solutions Page
const SolutionsPage = () => {
  const [ref, inView] = useInView();
  const navigate = useNavigate();

  const solutions = [
    { name: 'AI Consulting', path: '/ai-consultant', items: ['AI system design', 'LLM pipeline development', 'Automation workflows', 'Performance optimization'] },
    { name: 'Problem Solving', path: '/consultation', items: ['Complex engineering challenges', 'System architecture', 'Performance optimization', 'Technical debt reduction'] },
    { name: 'Technology Intelligence', path: '/consultation', items: ['Patent analytics', 'Competitor analysis', 'Trend mapping', 'IP strategy'] }
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24 relative" data-testid="solutions-page">
      <div className="container relative z-10">
        <div ref={ref} className={`mb-20 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="section-label">Solutions</div>
          <h1 className="font-display text-5xl md:text-7xl">How We Help<TitleOrb /></h1>
        </div>

        <ChromaGrid
          items={solutions.map((sol, i) => ({
            id: sol.name,
            label: `0${i + 1}`,
            title: sol.name,
            description: sol.items.join(' · '),
            borderColor: '#ff9500',
            gradient: 'linear-gradient(145deg, #0f1011, #0a0a0b)',
            path: sol.path,
          }))}
          columns={3}
          onCardClick={(item) => navigate(item.path)}
          className="mb-20"
        />

        <div className="divider mb-12" />

        <div className="text-center">
          <h2 className="font-display text-3xl mb-6">Ready to get started?</h2>
          <div className="flex justify-center gap-4">
            <button onClick={() => navigate('/ai-consultant')} className="btn-primary" data-testid="start-ai-consultant-btn">
              Try AI Consultant
            </button>
            <button onClick={() => navigate('/consultation')} className="btn-secondary" data-testid="start-consultation-btn">
              Talk to a Human
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Razorpay script loader
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Pricing Page
const PricingPage = () => {
  const [ref, inView] = useInView();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, products: ['PotPal', 'Ora'] })
      });
      if (res.ok) setSubmitted(true);
      else alert('Something went wrong. Please try again.');
    } catch { alert('Failed to join waitlist. Please try again.'); }
    setSubmitting(false);
  };

  const products = [
    { name: 'PotPal', desc: 'Self-aware AI plant care system', icon: Shield },
    { name: 'Ora', desc: 'Intelligent research & analysis platform', icon: Zap }
  ];

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="pricing-page">
      <div className="container max-w-3xl">
        <div ref={ref} className={`text-center mb-16 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#333] mb-8">
            <Clock size={14} className="text-[#ff9500]" />
            <span className="text-sm text-[#888]">Coming Soon</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl mb-4">Pricing</h1>
          <p className="text-[#888] text-lg">PotPal & Ora are launching soon</p>
          <p className="text-[#555] text-sm mt-2">Other products have independent pricing on their respective platforms</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {products.map((product, i) => (
            <div
              key={product.name}
              className={`card p-8 text-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              <product.icon size={32} className="text-[#ff9500] mx-auto mb-4" />
              <h3 className="font-display text-2xl mb-2">{product.name}</h3>
              <p className="text-[#555] text-sm">{product.desc}</p>
            </div>
          ))}
        </div>

        <div className={`card p-10 text-center transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.3s' }}>
          {submitted ? (
            <div>
              <Check size={40} className="text-green-500 mx-auto mb-4" />
              <h3 className="font-display text-2xl mb-2">You're on the list!</h3>
              <p className="text-[#888] text-sm">We'll notify you as soon as PotPal & Ora launch with pricing details.</p>
            </div>
          ) : (
            <div>
              <h3 className="font-display text-2xl mb-2">Join the Waitlist</h3>
              <p className="text-[#888] text-sm mb-6">Be the first to know when PotPal & Ora launch. Early access members get exclusive pricing.</p>
              <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-[#111] border border-[#222] rounded-lg text-white text-sm focus:outline-none focus:border-[#ff9500] transition-colors"
                  required
                />
                <button type="submit" className="btn-primary whitespace-nowrap" disabled={submitting}>
                  {submitting ? 'Joining...' : 'Join Waitlist'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Auth Pages
const LoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  // Auto-redirect when auth completes (covers both popup and redirect flows)
  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleGoogleLogin = async () => {
    setError('');
    setSigningIn(true);
    try {
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged + syncUserWithBackend will handle the rest
    } catch (err) {
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        // Fall back to redirect
        await signInWithRedirect(auth, googleProvider);
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorised. Please contact support@ioteverythin.com');
      } else {
        setError('Sign-in failed. Please try again.');
        console.error('Sign-in error:', err);
      }
      setSigningIn(false);
    }
  };

  return (
    <div className="animate-page-enter min-h-screen flex items-center justify-center pt-20 pb-20" data-testid="login-page">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-12">
          <span className="text-sm tracking-widest uppercase block mb-8">ioteverythin</span>
          <h1 className="font-display text-4xl mb-4">Welcome back</h1>
          <p className="text-[#888] text-sm">Sign in to your account</p>
        </div>

        <div className="card p-8">
          {error && <p className="text-red-400 text-sm mb-4 text-center">{error}</p>}
          <button 
            onClick={handleGoogleLogin}
            disabled={signingIn}
            className="w-full btn-secondary flex items-center justify-center gap-3 disabled:opacity-50"
            data-testid="google-login-btn"
          >
            {signingIn ? <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin" /> : (
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {signingIn ? 'Signing in...' : 'Continue with Google'}
          </button>
        </div>

        <p className="text-center text-sm text-[#555] mt-8">
          No account?{' '}
          <button onClick={() => navigate('/register')} className="text-white hover:text-[#888] transition-colors">
            Create one
          </button>
        </p>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleGoogleLogin = async () => {
    setError('');
    setSigningIn(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        await signInWithRedirect(auth, googleProvider);
      } else if (err.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorised. Please contact support@ioteverythin.com');
      } else {
        setError('Sign-in failed. Please try again.');
        console.error('Sign-in error:', err);
      }
      setSigningIn(false);
    }
  };

  return (
    <div className="animate-page-enter min-h-screen flex items-center justify-center pt-20 pb-20" data-testid="register-page">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-12">
          <span className="text-sm tracking-widest uppercase block mb-8">ioteverythin</span>
          <h1 className="font-display text-4xl mb-4">Create account</h1>
          <p className="text-[#888] text-sm">Join the ioteverythin ecosystem</p>
        </div>

        <div className="card p-8">
          {error && <p className="text-red-400 text-sm mb-4 text-center">{error}</p>}
          <button 
            onClick={handleGoogleLogin}
            disabled={signingIn}
            className="w-full btn-secondary flex items-center justify-center gap-3 disabled:opacity-50"
            data-testid="google-register-btn"
          >
            {signingIn ? <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin" /> : (
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {signingIn ? 'Signing in...' : 'Continue with Google'}
          </button>
        </div>

        <p className="text-center text-sm text-[#555] mt-8">
          Already have an account?{' '}
          <button onClick={() => navigate('/login')} className="text-white hover:text-[#888] transition-colors">
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

// Auth Callback (legacy - redirects to dashboard since Firebase uses popup flow)
const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-6 h-6 border border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#888] text-sm">Redirecting...</p>
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [feedback, setFeedback] = useState([]);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [loading, setLoading] = useState(true);

  const currentUser = location.state?.user || user;

  useEffect(() => {
    if (!currentUser && !location.state?.user) {
      const checkAuth = async () => {
        try {
          const response = await fetch(`${API_URL}/auth/me`, { credentials: 'include' });
          if (!response.ok) {
            navigate('/login');
          }
        } catch {
          navigate('/login');
        } finally {
          setLoading(false);
        }
      };
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [currentUser, navigate, location.state]);

  useEffect(() => {
    if (activeTab === 'feedback') {
      fetchFeedback();
    }
  }, [activeTab]);

  const fetchFeedback = async () => {
    try {
      const response = await fetch(`${API_URL}/feedback`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFeedback(data);
      }
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    }
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackRating || !feedbackMessage.trim()) return;

    try {
      const response = await fetch(`${API_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rating: feedbackRating, message: feedbackMessage })
      });

      if (response.ok) {
        setFeedbackRating(0);
        setFeedbackMessage('');
        fetchFeedback();
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--abyss-black)]">
        <svg
          width="64"
          height="64"
          viewBox="0 0 100 100"
          className="animate-pulse-subtle"
          style={{ filter: 'brightness(1.2)' }}
        >
          <circle
            cx="50"
            cy="55"
            r="35"
            fill="none"
            stroke="#FF8C00"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray="180 220"
            transform="rotate(-90 50 50)"
          />
          <line
            x1="50"
            y1="20"
            x2="50"
            y2="45"
            stroke="#FF8C00"
            strokeWidth="6"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }

  const sidebarItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'products', icon: Package, label: 'Products' },
    { id: 'billing', icon: CreditCard, label: 'Billing' },
    { id: 'feedback', icon: MessageSquare, label: 'Feedback' },
    { id: 'settings', icon: Settings, label: 'Settings' }
  ];

  return (
    <div className="animate-page-enter min-h-screen pt-20" data-testid="dashboard-page">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 border-r border-[#1a1a1a] min-h-[calc(100vh-80px)] p-6 hidden md:block">
          <div className="mb-8">
            <p className="font-medium mb-1">{currentUser?.name || 'User'}</p>
            <p className="text-sm text-[#555]">{currentUser?.email}</p>
            <span className="badge mt-3">{currentUser?.plan || 'Free'}</span>
          </div>
          
          <nav className="space-y-1">
            {sidebarItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 text-sm transition-colors ${
                  activeTab === item.id ? 'text-white bg-[#111]' : 'text-[#555] hover:text-white'
                }`}
                data-testid={`sidebar-${item.id}`}
              >
                <item.icon size={16} />
                {item.label}
              </button>
            ))}
          </nav>

          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-3 text-sm text-[#555] hover:text-white transition-colors mt-4"
            data-testid="sidebar-logout"
          >
            <LogOut size={16} />
            Logout
          </button>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          {activeTab === 'overview' && (
            <div>
              <h1 className="font-display text-3xl mb-8">Overview</h1>
              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                {[
                  { label: 'Active Plan', value: currentUser?.plan || 'Free' },
                  { label: 'Tools Available', value: currentUser?.plan === 'Free' ? '2' : 'All' },
                  { label: 'API Usage', value: `0 / ${currentUser?.plan === 'Free' ? '5' : '∞'}` }
                ].map(item => (
                  <div key={item.label} className="card p-6">
                    <p className="text-xs text-[#555] uppercase tracking-wider mb-2">{item.label}</p>
                    <p className="text-2xl font-display">{item.value}</p>
                  </div>
                ))}
              </div>
              <div className="card p-6">
                <h2 className="text-xs text-[#555] uppercase tracking-wider mb-4">Recent Activity</h2>
                <p className="text-[#555] text-sm">No recent activity</p>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div>
              <h1 className="font-display text-3xl mb-8">My Products</h1>
              {currentUser?.plan === 'Free' && (
                <div className="card p-6 mb-8 border-[#333]">
                  <p className="text-sm mb-4">Upgrade to Pro to access all products</p>
                  <button onClick={() => navigate('/pricing')} className="btn-primary text-xs">
                    Upgrade Now
                  </button>
                </div>
              )}
              <div className="space-y-4">
                {[
                  { name: 'OpenDocs', url: 'https://github.com/ioteverythin/OpenDocs' },
                  { name: 'ProphetForge', url: 'https://www.prophetforge.com/' },
                ].map((product, i) => (
                  <div key={product.name} className="card p-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-[#555]">0{i + 1}</span>
                      <span>{product.name}</span>
                    </div>
                    <a href={product.url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs py-2 px-4">Open</a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div>
              <h1 className="font-display text-3xl mb-8">Billing</h1>
              <div className="card p-8 mb-8">
                <h2 className="text-xs text-[#555] uppercase tracking-wider mb-4">Current Plan</h2>
                <p className="font-display text-4xl mb-6">{currentUser?.plan || 'Free'}</p>
                <button onClick={() => navigate('/pricing')} className="btn-primary">
                  {currentUser?.plan === 'Free' ? 'Upgrade Plan' : 'Change Plan'}
                </button>
              </div>
              <div className="card p-8">
                <h2 className="text-xs text-[#555] uppercase tracking-wider mb-4">Payment History</h2>
                <p className="text-[#555] text-sm">No payment history</p>
              </div>
            </div>
          )}

          {activeTab === 'feedback' && (
            <div>
              <h1 className="font-display text-3xl mb-8">Feedback</h1>
              <div className="card p-8 mb-8">
                <h2 className="text-xs text-[#555] uppercase tracking-wider mb-6">Submit Feedback</h2>
                <form onSubmit={submitFeedback}>
                  <div className="mb-6">
                    <p className="text-sm text-[#555] mb-3">Rating</p>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setFeedbackRating(n)}
                          className={`w-10 h-10 border transition-colors ${
                            feedbackRating >= n ? 'bg-white text-black border-white' : 'border-[#1a1a1a] text-[#555] hover:border-[#333]'
                          }`}
                          data-testid={`rating-${n}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    placeholder="Share your feedback..."
                    className="input mb-6 h-24 resize-none"
                    data-testid="feedback-textarea"
                  />
                  <button type="submit" className="btn-primary" data-testid="submit-feedback-btn">
                    Submit Feedback
                  </button>
                </form>
              </div>
              
              {feedback.length > 0 && (
                <div className="card p-8">
                  <h2 className="text-xs text-[#555] uppercase tracking-wider mb-6">Your Feedback</h2>
                  <div className="space-y-4">
                    {feedback.map((f, i) => (
                      <div key={i} className="p-4 border border-[#1a1a1a]">
                        <div className="flex items-center gap-1 mb-2">
                          {Array.from({ length: f.rating }).map((_, j) => (
                            <Star key={j} size={12} className="fill-white" />
                          ))}
                        </div>
                        <p className="text-sm mb-2">{f.message}</p>
                        <p className="text-xs text-[#555]">{new Date(f.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div>
              <h1 className="font-display text-3xl mb-8">Settings</h1>
              <div className="card p-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Name</label>
                    <input type="text" value={currentUser?.name || ''} className="input" readOnly />
                  </div>
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Email</label>
                    <input type="email" value={currentUser?.email || ''} className="input" readOnly />
                  </div>
                  <button className="btn-primary">Update Profile</button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// AI Consultant Page
const AIConsultantPage = () => {
  const [activeTab, setActiveTab] = useState('idea');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [ideaForm, setIdeaForm] = useState({ name: '', email: '', idea: '' });
  const [crawlForm, setCrawlForm] = useState({ name: '', email: '', website_url: '' });
  const [pocForm, setPocForm] = useState({ name: '', email: '', poc_description: '', repo_url: '', tech_stack: '' });

  const tabs = [
    { id: 'idea', label: 'Submit an Idea', icon: <Zap size={20} className="text-white" />, desc: 'Describe your idea. Our AI will analyze feasibility & provide instant costing' },
    { id: 'crawl', label: 'Analyze My Website', icon: <Search size={20} className="text-white" />, desc: 'Paste your website URL. Our AI agent crawls it and generates AI opportunities' },
    { id: 'poc', label: 'POC → Production', icon: <Layers size={20} className="text-white" />, desc: 'Have a proof of concept? We\'ll create a production roadmap with costing' }
  ];

  const handleSubmitIdea = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/ai-consultant/analyze-idea`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ideaForm)
      });
      if (res.ok) { const data = await res.json(); setResult(data.analysis); }
      else { const err = await res.json(); alert(err.detail || 'Failed'); }
    } catch { alert('Something went wrong.'); }
    setLoading(false);
  };

  const handleCrawl = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/ai-consultant/crawl-website`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(crawlForm)
      });
      if (res.ok) { const data = await res.json(); setResult(data.analysis); }
      else { const err = await res.json(); alert(err.detail || 'Failed to crawl website'); }
    } catch { alert('Something went wrong.'); }
    setLoading(false);
  };

  const handlePoc = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/ai-consultant/poc-to-production`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pocForm)
      });
      if (res.ok) { const data = await res.json(); setResult(data.analysis); }
      else { const err = await res.json(); alert(err.detail || 'Failed'); }
    } catch { alert('Something went wrong.'); }
    setLoading(false);
  };

  const switchTab = (id) => { setActiveTab(id); setResult(null); };

  // Simple markdown-to-html: headers, bold, lists
  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-white mt-6 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold text-white mt-8 mb-3">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold text-white mt-8 mb-4">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-[#aaa]">$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal text-[#aaa]">$1</li>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="ai-consultant-page">
      <div className="container max-w-4xl">
        {/* Header */}
        <div className="mb-16">
          <div className="section-label">AI-Powered</div>
          <h1 className="font-display text-5xl md:text-7xl mb-4">AI Consultant<TitleOrb /></h1>
          <p className="text-[#888] max-w-xl">Get instant AI-powered analysis, costing estimates, and strategic recommendations. Our team follows up within 24 hours.</p>
        </div>

        {/* Tab Selector */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => switchTab(tab.id)}
              className={`card p-6 text-left transition-all duration-300 hover:border-[#555] ${
                activeTab === tab.id ? 'border-white bg-[#1a1a1a]' : ''
              }`}
              data-testid={`ai-tab-${tab.id}`}
            >
              <span className="w-10 h-10 rounded-full border border-[#333] flex items-center justify-center mb-3">{tab.icon}</span>
              <span className="text-sm font-medium text-white block mb-1">{tab.label}</span>
              <span className="text-xs text-[#666] leading-relaxed">{tab.desc}</span>
            </button>
          ))}
        </div>

        {/* Forms */}
        <div className="card p-8 mb-8">
          {activeTab === 'idea' && (
            <form onSubmit={handleSubmitIdea}>
              <h2 className="text-lg font-medium mb-6 flex items-center gap-2"><Zap size={18} className="text-white" /> Describe Your Idea</h2>
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Name *</label>
                    <input type="text" required className="input" value={ideaForm.name} onChange={e => setIdeaForm({...ideaForm, name: e.target.value})} data-testid="ai-idea-name" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Email *</label>
                    <input type="email" required className="input" value={ideaForm.email} onChange={e => setIdeaForm({...ideaForm, email: e.target.value})} data-testid="ai-idea-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Your Idea *</label>
                  <textarea required rows={6} className="input resize-none" placeholder="Describe what you want to build, the problem it solves, your target audience, any tech preferences..." value={ideaForm.idea} onChange={e => setIdeaForm({...ideaForm, idea: e.target.value})} data-testid="ai-idea-text" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="ai-idea-submit">
                  {loading ? <span className="flex items-center justify-center gap-2"><Activity size={16} className="animate-spin" /> AI is analyzing...</span> : 'Get AI Analysis & Costing'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'crawl' && (
            <form onSubmit={handleCrawl}>
              <h2 className="text-lg font-medium mb-6 flex items-center gap-2"><Search size={18} className="text-white" /> Analyze Your Website</h2>
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Name *</label>
                    <input type="text" required className="input" value={crawlForm.name} onChange={e => setCrawlForm({...crawlForm, name: e.target.value})} data-testid="ai-crawl-name" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Email *</label>
                    <input type="email" required className="input" value={crawlForm.email} onChange={e => setCrawlForm({...crawlForm, email: e.target.value})} data-testid="ai-crawl-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Website URL *</label>
                  <input type="url" required className="input" placeholder="https://yourcompany.com" value={crawlForm.website_url} onChange={e => setCrawlForm({...crawlForm, website_url: e.target.value})} data-testid="ai-crawl-url" />
                  <p className="text-xs text-[#555] mt-2">Our AI agent will crawl your site to understand your business and suggest tailored AI opportunities.</p>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="ai-crawl-submit">
                  {loading ? <span className="flex items-center justify-center gap-2"><Activity size={16} className="animate-spin" /> Crawling & analyzing...</span> : 'Crawl & Generate AI Ideas'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'poc' && (
            <form onSubmit={handlePoc}>
              <h2 className="text-lg font-medium mb-6 flex items-center gap-2"><Layers size={18} className="text-white" /> POC → Production</h2>
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Name *</label>
                    <input type="text" required className="input" value={pocForm.name} onChange={e => setPocForm({...pocForm, name: e.target.value})} data-testid="ai-poc-name" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Email *</label>
                    <input type="email" required className="input" value={pocForm.email} onChange={e => setPocForm({...pocForm, email: e.target.value})} data-testid="ai-poc-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">POC Description *</label>
                  <textarea required rows={5} className="input resize-none" placeholder="Describe your POC: what it does, current state, what needs to change for production..." value={pocForm.poc_description} onChange={e => setPocForm({...pocForm, poc_description: e.target.value})} data-testid="ai-poc-desc" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Repo URL</label>
                    <input type="url" className="input" placeholder="https://github.com/..." value={pocForm.repo_url} onChange={e => setPocForm({...pocForm, repo_url: e.target.value})} data-testid="ai-poc-repo" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Tech Stack</label>
                    <input type="text" className="input" placeholder="e.g. React, Python, AWS" value={pocForm.tech_stack} onChange={e => setPocForm({...pocForm, tech_stack: e.target.value})} data-testid="ai-poc-stack" />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="ai-poc-submit">
                  {loading ? <span className="flex items-center justify-center gap-2"><Activity size={16} className="animate-spin" /> Generating roadmap...</span> : 'Get Production Roadmap & Costing'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* AI Response */}
        {result && (
          <div className="card p-8 animate-fade-in" data-testid="ai-result">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#1a1a1a]">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Brain size={16} className="text-blue-400" />
              </div>
              <div>
                <span className="text-sm font-medium text-white">AI Analysis</span>
                <span className="text-xs text-[#555] block">Powered by GPT-4o · Our team has been notified and will follow up</span>
              </div>
            </div>
            <div
              className="prose prose-invert max-w-none text-sm text-[#aaa] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(result) }}
            />
            <div className="mt-8 pt-6 border-t border-[#1a1a1a] flex flex-col sm:flex-row gap-4">
              <div className="flex-1 card p-4 bg-[#0a0a0a] border-blue-500/20">
                <span className="text-xs text-blue-400 uppercase tracking-wider block mb-1">What happens next?</span>
                <span className="text-xs text-[#888]">Our team reviews this analysis and reaches out within 24 hours with a tailored proposal.</span>
              </div>
              <button onClick={() => { setResult(null); }} className="btn-secondary text-xs self-start" data-testid="ai-new-query">
                Start New Query
              </button>
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && !result && (
          <div className="card p-12 text-center" data-testid="ai-loading">
            <Activity size={32} className="animate-spin text-blue-400 mx-auto mb-4" />
            <p className="text-sm text-[#888]">Our AI is analyzing your input...</p>
            <p className="text-xs text-[#555] mt-2">This usually takes 10-20 seconds</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Consultation Page
const ConsultationPage = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', description: '', budget: '', timeline: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/consultations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (response.ok) setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit consultation:', error);
    }
  };

  if (submitted) {
    return (
      <div className="animate-page-enter min-h-screen flex items-center justify-center pt-20" data-testid="consultation-success">
        <div className="text-center">
          <div className="w-16 h-16 border border-white rounded-full flex items-center justify-center mx-auto mb-8">
            <Check size={24} />
          </div>
          <h1 className="font-display text-3xl mb-4">Request Received</h1>
          <p className="text-[#888] mb-8">We'll be in touch within 24 hours.</p>
          <button onClick={() => navigate('/')} className="btn-primary" data-testid="back-home-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-page-enter pt-32 pb-24" data-testid="consultation-page">
      <div className="container max-w-xl">
        <div className="mb-12">
          <div className="section-label">Consulting</div>
          <h1 className="font-display text-4xl md:text-5xl">Start a Consultation</h1>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="consultation-name" />
              </div>
              <div>
                <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Email *</label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="consultation-email" />
              </div>
              <div>
                <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Problem Description *</label>
                <textarea required rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input resize-none" data-testid="consultation-description" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Budget</label>
                  <input type="text" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="input" placeholder="Optional" data-testid="consultation-budget" />
                </div>
                <div>
                  <label className="block text-xs text-[#555] uppercase tracking-wider mb-2">Timeline</label>
                  <input type="text" value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} className="input" placeholder="Optional" data-testid="consultation-timeline" />
                </div>
              </div>
              <button type="submit" className="btn-primary w-full" data-testid="submit-consultation-btn">
                Submit Request
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Chat Widget - floating bottom-right chat panel (Matt).
// Replaced on the live site by the Voxi voice widget embedded in public/index.html.
// Kept here (unmounted) so the /api/ai-chat backend UI can be restored if needed.
// eslint-disable-next-line no-unused-vars
const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add welcome message on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Hey! I'm Matt, your ioteverythin AI assistant. Ask me anything about our products, services, or how we can help with your AI project.",
        suggestions: ["What products do you offer?", "How does AI consulting work?", "Tell me about PotPal"],
      }]);
    }
  }, [open, messages.length]);

  // Linkify known pages/products inside assistant text
  const renderContent = (text) => {
    const LINKS = [
      { re: /\bPotPal\b/g,       to: '/products/potpal', ext: false },
      { re: /\bOra\b|\bORA\b/g,  to: '/products/ora',    ext: false },
      { re: /\bOpenDocs\b/g,     to: 'https://github.com/ioteverythin/OpenDocs', ext: true },
      { re: /\bProphetForge\b/g, to: 'https://www.prophetforge.com/', ext: true },
      { re: /\bAI Consultant\b/gi, to: '/ai-consultant',  ext: false },
      { re: /\bconsultation\b/gi,  to: '/consultation',   ext: false },
      { re: /\bresearch\b/gi,      to: '/research',        ext: false },
      { re: /\bopen[- ]source\b/gi,to: '/open-source',    ext: false },
      { re: /\bplatform\b/gi,      to: '/platform',        ext: false },
      { re: /\bwaitlist\b/gi,      to: '/pricing',         ext: false },
      { re: /\bsupport ticket\b/gi,to: '/support',         ext: false },
    ];
    const found = [];
    for (const { re, to, ext } of LINKS) {
      let m;
      while ((m = re.exec(text)) !== null) {
        found.push({ start: m.index, end: m.index + m[0].length, word: m[0], to, ext });
      }
    }
    found.sort((a, b) => a.start - b.start);
    // deduplicate overlaps
    const clean = [];
    let cursor = 0;
    for (const f of found) {
      if (f.start >= cursor) { clean.push(f); cursor = f.end; }
    }
    const els = [];
    let pos = 0;
    for (const f of clean) {
      if (pos < f.start) els.push(text.slice(pos, f.start));
      if (f.ext) {
        els.push(<a key={f.start} href={f.to} target="_blank" rel="noopener noreferrer" className="text-[#ff9500] hover:underline font-medium">{f.word}</a>);
      } else {
        els.push(<Link key={f.start} to={f.to} onClick={() => setOpen(false)} className="text-[#ff9500] hover:underline font-medium">{f.word}</Link>);
      }
      pos = f.end;
    }
    if (pos < text.length) els.push(text.slice(pos));
    return els;
  };

  const sendMessage = async (text) => {
    const userMsg = (text || input).trim();
    if (!userMsg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/ai-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, history: messages }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.reply, suggestions: data.suggestions || [] }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble right now. Try our AI Consultant page for detailed analysis.", suggestions: [] }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.', suggestions: [] }]);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => { e.preventDefault(); sendMessage(); };

  return (
    <div className="hidden sm:block fixed bottom-6 left-6 z-50">
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="group relative w-16 h-16 rounded-full bg-gradient-to-br from-[#1a1a1c] to-[#0a0a0b] border border-[#ff9500]/50 flex items-center justify-center shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_24px_6px_rgba(255,149,0,0.25)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_36px_10px_rgba(255,149,0,0.45)] hover:border-[#ff9500] transition-all duration-300 hover:scale-110"
          title="Chat with AI"
          data-testid="chat-toggle"
        >
          <span className="absolute -inset-1 rounded-full bg-[#ff9500]/30 blur-md animate-ping" />
          <span className="absolute inset-0 rounded-full bg-gradient-to-br from-[#ff9500]/20 to-transparent" />
          <span className="relative z-10 w-10 h-10 rounded-full bg-[#0a0a0b]/60 flex items-center justify-center border border-[#ff9500]/20 group-hover:border-[#ff9500]/40 transition-colors">
            <MessageSquare size={18} className="text-[#ff9500] drop-shadow-[0_0_6px_rgba(255,149,0,0.6)]" />
          </span>
        </button>
      )}

      {open && (
        <div className="chat-panel-enter w-80 h-[32rem] bg-[#0a0a0a] border border-[#222] rounded-lg flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-mono text-gray-400 tracking-wider">MATT — IOTE AI</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-gray-300 transition-colors">
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 chat-scrollbar">
            {messages.map((msg, i) => (
              <div key={i} className="chat-msg-enter">
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 ${
                    msg.role === 'user'
                      ? 'bg-[#1a1a1a] border border-[#333] text-gray-200'
                      : 'bg-[#111] border border-[#1a1a1a] text-gray-400'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <Brain size={10} className="text-blue-400" />
                        <span className="text-[9px] text-blue-400/60 font-mono">IOTE AI</span>
                      </div>
                    )}
                    <p className="text-xs leading-relaxed whitespace-pre-wrap">
                      {msg.role === 'assistant' ? renderContent(msg.content) : msg.content}
                    </p>
                  </div>
                </div>
                {/* Suggestion chips — only under last assistant message */}
                {msg.role === 'assistant' && i === messages.length - 1 && msg.suggestions?.length > 0 && !loading && (
                  <div className="mt-2 flex flex-wrap gap-1.5 pl-1">
                    {msg.suggestions.map((s, si) => (
                      <button
                        key={si}
                        onClick={() => sendMessage(s)}
                        className="text-[10px] px-2.5 py-1 rounded-full bg-[#0d0d0e] border border-[#ff9500]/25 text-[#ff9500]/70 hover:text-[#ff9500] hover:border-[#ff9500]/60 hover:bg-[#ff9500]/5 transition-all font-mono leading-none"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#111] border border-[#1a1a1a] rounded-lg px-3 py-2">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                    <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                    <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 border-t border-[#1a1a1a] flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask anything..."
              maxLength={2000}
              className="flex-1 bg-[#111] border border-[#222] rounded px-3 py-1.5 text-xs text-gray-300 placeholder-gray-600 focus:outline-none focus:border-[#333] font-mono"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="px-2.5 py-1.5 bg-[#1a1a1a] border border-[#333] rounded text-gray-400 hover:text-gray-200 hover:border-[#444] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              data-testid="chat-send"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// Global Floating Particles - small greyish dots across entire website
const GlobalParticles = () => {
  const [particles] = useState(() => 
    Array.from({ length: 55 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: 1 + Math.random() * 2,
      duration: 18 + Math.random() * 25,
      delay: Math.random() * 15,
      opacity: 0.06 + Math.random() * 0.12
    }))
  );

  return (
    <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            background: '#888',
            opacity: p.opacity,
            animation: `floatParticleGlobal ${p.duration}s ease-in-out infinite`,
            animationDelay: `${p.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// Main App Router
const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:productId" element={<ProductDetailPage />} />
      <Route path="/open-source" element={<OpenSourcePage />} />
      <Route path="/research" element={<ResearchPage />} />
      <Route path="/research/cue-v5" element={<CueV5Page />} />
      {/* Old link kept working: the page was published at /cue-v4 before the v5 preprint. */}
      <Route path="/research/cue-v4" element={<Navigate to="/research/cue-v5" replace />} />
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/solutions" element={<SolutionsPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/consultation" element={<ConsultationPage />} />
      <Route path="/ai-consultant" element={<AIConsultantPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/support" element={<SupportPage />} />
    </Routes>
  );
};

// Loading intro — fades title in, holds, then fades the overlay out.
// Shop Page (placeholder — to be built out)

// ==================== SUPPORT TICKET PAGE ====================
const SUPPORT_PRODUCTS = [
  { value: 'potpal', label: 'PotPal' },
  { value: 'ora', label: 'ORA' },
  { value: 'opendocs', label: 'OpenDocs' },
  { value: 'prophetforge', label: 'ProphetForge' },
  { value: 'platform', label: 'IoTE Platform' },
  { value: 'other', label: 'Other' },
];

const SupportPage = () => {
  const [form, setForm] = React.useState({ name: '', email: '', product: '', subject: '', description: '' });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(null); // ticket ref on success
  const [error, setError] = React.useState('');

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.product) { setError('Please select a product.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/support-tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = Array.isArray(data.detail)
          ? data.detail.map(d => d.msg || JSON.stringify(d)).join(', ')
          : (typeof data.detail === 'string' ? data.detail : null);
        throw new Error(detail || 'Submission failed. Please try again.');
      }
      const data = await res.json();
      setSubmitted(data.ticket_ref);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="animate-page-enter pt-32 pb-24">
        <div className="container max-w-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-[#ff9500]/10 border border-[#ff9500]/30 flex items-center justify-center mx-auto mb-8">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ff9500" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <div className="section-label">Submitted</div>
          <h1 className="font-display text-4xl md:text-5xl mb-6">Ticket #{submitted}</h1>
          <p className="text-[#888] mb-8 max-w-md mx-auto">Your support ticket has been received. Our team will respond to <span className="text-white">{form.email}</span> as soon as possible.</p>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-page-enter pt-32 pb-24">
      <div className="container max-w-2xl">
        <div className="section-label">Help &amp; Support</div>
        <h1 className="font-display text-5xl md:text-6xl mb-4">Raise a Ticket<TitleOrb /></h1>
        <p className="text-[#888] mb-12">Having an issue? Fill in the form below and our team will get back to you.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product selector */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#555] mb-2">Product / Area *</label>
            <select
              name="product"
              value={form.product}
              onChange={handleChange}
              required
              className="w-full bg-[#0d0d0e] border border-[#222] rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#ff9500]/60 transition-colors appearance-none"
            >
              <option value="" disabled>Select a product&hellip;</option>
              {SUPPORT_PRODUCTS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Name + Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#555] mb-2">Your Name *</label>
              <input
                type="text" name="name" value={form.name} onChange={handleChange}
                required placeholder="Jane Smith"
                className="w-full bg-[#0d0d0e] border border-[#222] rounded-lg px-4 py-3 text-white text-sm placeholder-[#444] focus:outline-none focus:border-[#ff9500]/60 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#555] mb-2">Email *</label>
              <input
                type="email" name="email" value={form.email} onChange={handleChange}
                required placeholder="jane@example.com"
                className="w-full bg-[#0d0d0e] border border-[#222] rounded-lg px-4 py-3 text-white text-sm placeholder-[#444] focus:outline-none focus:border-[#ff9500]/60 transition-colors"
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#555] mb-2">Subject *</label>
            <input
              type="text" name="subject" value={form.subject} onChange={handleChange}
              required placeholder="Brief summary of the issue"
              className="w-full bg-[#0d0d0e] border border-[#222] rounded-lg px-4 py-3 text-white text-sm placeholder-[#444] focus:outline-none focus:border-[#ff9500]/60 transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#555] mb-2">Description *</label>
            <textarea
              name="description" value={form.description} onChange={handleChange}
              required rows={6} placeholder="Describe the issue in detail — steps to reproduce, expected vs actual behaviour, screenshots, etc."
              className="w-full bg-[#0d0d0e] border border-[#222] rounded-lg px-4 py-3 text-white text-sm placeholder-[#444] focus:outline-none focus:border-[#ff9500]/60 transition-colors resize-none"
            />
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <button type="submit" disabled={submitting} className="btn-primary w-full sm:w-auto disabled:opacity-50">
            {submitting ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== TERMS & CONDITIONS PAGE ====================
const TermsPage = () => (
  <div className="animate-page-enter pt-32 pb-24">
    <div className="container max-w-3xl">
      <div className="section-label">Legal</div>
      <h1 className="font-display text-5xl md:text-6xl mb-12">Terms &amp; Conditions<TitleOrb /></h1>
      <div className="prose prose-invert max-w-none space-y-10 text-[#aaa] text-sm leading-relaxed">

        <section>
          <h2 className="font-display text-2xl text-white mb-4">1. Acceptance of Terms</h2>
          <p>By accessing or using any product, service, or website operated by IOTEVERYTHIN (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;), you agree to be bound by these Terms and Conditions. If you do not agree, please discontinue use immediately.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">2. Products and Services</h2>
          <p>IOTEVERYTHIN offers AI-native hardware and software products including, but not limited to, PotPal, ORA, OpenDocs, and ProphetForge. All products are subject to availability. Specifications, pricing, and features are subject to change without notice.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">3. Intellectual Property</h2>
          <p>All content, trademarks, patents, software, and hardware designs on this website are the exclusive property of IOTEVERYTHIN or its licensors. You may not reproduce, distribute, or create derivative works without prior written consent.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">4. User Accounts</h2>
          <p>If you create an account with us, you are responsible for maintaining the confidentiality of your credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorised use at support@ioteverythin.com.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">5. Payments and Refunds</h2>
          <p>All purchases are processed securely. Prices are listed in Indian Rupees (INR) unless stated otherwise. Refund eligibility is assessed on a case-by-case basis. For refund requests, contact support@ioteverythin.com within 7 days of purchase.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">6. Privacy</h2>
          <p>We collect and process personal data in accordance with our Privacy Policy. By using our services you consent to such processing. We do not sell your personal data to third parties.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">7. Disclaimer of Warranties</h2>
          <p>Our products and services are provided &quot;as is&quot; without warranties of any kind, express or implied. We do not warrant that services will be uninterrupted or error-free.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">8. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, IOTEVERYTHIN shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our products or services.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">9. Governing Law</h2>
          <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Bangalore, Karnataka.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-white mb-4">10. Contact</h2>
          <p>For any queries regarding these Terms, please contact us at <a href="mailto:support@ioteverythin.com" className="text-[#ff9500] hover:underline">support@ioteverythin.com</a>.</p>
        </section>

        <p className="text-[#555] text-xs pt-8 border-t border-[#1a1a1a]">Last updated: May 2026</p>
      </div>
    </div>
  </div>
);

// Shop product data
const formatPrice = (paise) => {
  if (paise === null) return 'Contact Sales';
  if (paise === 0) return 'Free';
  return '₹' + (paise / 100).toLocaleString('en-IN');
};

const SHOP_PRODUCTS = [
  {
    id: 'opendocs',
    name: 'OpenDocs',
    tagline: 'Multi-format documentation generator',
    description: 'Generate accurate, multi-format technical documentation from any source: API specs, code, architecture diagrams.',
    icon: Code,
    tiers: [
      { id: 'opendocs-free', label: 'Free', price: 0, period: null, features: ['5 docs/month', 'Markdown output', 'Community support'] },
      { id: 'opendocs-pro', label: 'Pro', price: 199900, period: '/mo', features: ['Unlimited docs', 'All formats (PDF, Docx, HTML)', 'API access', 'Priority support'] },
      { id: 'opendocs-team', label: 'Team', price: 499900, period: '/mo', features: ['Everything in Pro', '10 seats included', 'SSO & audit logs', 'Custom branding'] },
    ],
  },
  {
    id: 'prophetforge',
    name: 'ProphetForge',
    tagline: 'AI trading intelligence platform',
    description: 'AI-native trading intelligence that spots signal in noise, backtested across decades of market data.',
    icon: Activity,
    tiers: [
      { id: 'prophetforge-starter', label: 'Starter', price: 299900, period: '/mo', features: ['5 strategies', 'Daily signals', 'Basic backtesting', 'Email alerts'] },
      { id: 'prophetforge-pro', label: 'Pro', price: 799900, period: '/mo', features: ['Unlimited strategies', 'Real-time signals', 'Advanced backtesting', 'API & webhooks'] },
      { id: 'prophetforge-fund', label: 'Fund', price: null, period: null, features: ['White-label deployment', 'Custom models', 'Dedicated infrastructure', 'SLA & compliance'] },
    ],
  },
  {
    id: 'potpal',
    name: 'PotPal',
    tagline: 'Self-aware AI plant care system',
    description: 'Monitor, understand, and respond to each plant\'s needs in real time with adaptive AI-driven watering, lighting, and climate control.',
    icon: Shield,
    tiers: [
      { id: 'potpal-solo', label: 'Solo', price: 149900, period: '/mo', features: ['1-3 plants monitored', 'Automated watering', 'Basic AI light schedules', 'Mobile alerts'] },
      { id: 'potpal-firm', label: 'Grower', price: 499900, period: '/mo', features: ['Up to 25 plants', 'Advanced predictive care', 'Adaptive grow-light control', 'Energy optimization'] },
      { id: 'potpal-enterprise', label: 'Enterprise', price: null, period: null, features: ['Large installation support', 'Custom sensor profiles', 'Home AI ecosystem integration', 'Dedicated support'] },
    ],
  },
  {
    id: 'ora',
    name: 'Ora',
    tagline: 'AI-powered ambient ring lamp',
    description: 'Sculpted ambient ring lamp with on-device AI Vision that paints your room with the mood of any photo, hour, or season.',
    icon: Zap,
    tiers: [
      { id: 'ora-dev', label: 'Developer', price: 0, period: null, features: ['1,000 minutes/mo', 'Community models', 'Playground access', 'Basic analytics'] },
      { id: 'ora-growth', label: 'Growth', price: 399900, period: '/mo', features: ['50,000 minutes/mo', 'All LLM backends', 'Custom voices', 'Webhooks & API'] },
      { id: 'ora-scale', label: 'Scale', price: null, period: null, features: ['Unlimited minutes', 'Dedicated infra', 'Custom SLA', 'White-label SDK'] },
    ],
  },
  {
    id: 'homeai',
    name: 'HomeAI',
    tagline: 'AI-powered home automation',
    description: 'Intelligent home automation that learns your patterns and adapts: energy optimization, security, comfort.',
    icon: Layers,
    tiers: [
      { id: 'homeai-hub', label: 'Hub', price: 1499900, period: ' one-time', features: ['HomeAI Hub device', '50 device connections', 'Local AI processing', 'Mobile app'] },
      { id: 'homeai-pro', label: 'Hub Pro', price: 2999900, period: ' one-time', features: ['Everything in Hub', 'Unlimited devices', 'Cloud AI sync', 'Voice control'] },
      { id: 'homeai-cloud', label: 'Cloud+', price: 49900, period: '/mo', features: ['Cloud AI features', 'Remote access', 'Advanced automations', 'Priority updates'] },
    ],
  },
];

const ShopProductCard = ({ product, onAddToCart }) => {
  const [selectedTier, setSelectedTier] = useState(0);
  const tier = product.tiers[selectedTier];

  return (
    <div
      className="rounded-2xl p-6 flex flex-col h-full"
      style={{
        background: 'var(--carbon-surface)',
        border: '1px solid var(--border-subtle)',
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'rgba(168, 144, 96, 0.3)';
        e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(168, 144, 96, 0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-subtle)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Icon */}
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
        style={{ background: 'var(--accent-gold-glow)', color: 'var(--accent-gold)' }}
      >
        <product.icon size={22} />
      </div>

      {/* Name + tagline */}
      <h3 className="text-xl font-medium text-white mb-1">{product.name}</h3>
      <p className="text-[#888] text-sm mb-5 leading-relaxed">{product.tagline}</p>

      {/* Tier pills */}
      <div className="flex gap-2 mb-5">
        {product.tiers.map((t, i) => (
          <button
            key={t.id}
            onClick={() => setSelectedTier(i)}
            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200"
            style={
              i === selectedTier
                ? { background: 'var(--accent-gold)', color: '#000' }
                : { background: 'transparent', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Price */}
      <div className="mb-5">
        <span className="text-3xl font-light text-white">{formatPrice(tier.price)}</span>
        {tier.period && <span className="text-sm text-[#888] ml-1">{tier.period}</span>}
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-6 flex-1">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-[#888]">
            <Check size={14} className="text-[#a89060] mt-0.5 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      {/* CTA */}
      {tier.price !== null ? (
        <button
          onClick={() => onAddToCart(product, tier)}
          className="w-full py-3 px-4 rounded-lg text-xs font-medium uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2"
          style={{ background: 'var(--text-primary)', color: 'var(--abyss-black)' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--text-secondary)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--text-primary)'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <ShoppingCart size={14} />
          Add to Cart
        </button>
      ) : (
        <button
          onClick={() => window.location.href = 'mailto:hello@ioteverythin.com'}
          className="btn-secondary w-full text-xs"
        >
          Contact Sales
        </button>
      )}
    </div>
  );
};

const SHOP_CATEGORIES = [
  { id: 'all', label: 'All Products', icon: Package },
  { id: 'free', label: 'Free Tier', icon: Tag },
  { id: 'subscription', label: 'Subscription', icon: CreditCard },
  { id: 'one-time', label: 'One-Time Purchase', icon: ShoppingCart },
  { id: 'enterprise', label: 'Enterprise', icon: Shield },
];

const filterProducts = (products, category, search) => {
  let filtered = products;
  if (category === 'free') filtered = products.filter(p => p.tiers.some(t => t.price === 0));
  else if (category === 'subscription') filtered = products.filter(p => p.tiers.some(t => t.period === '/mo'));
  else if (category === 'one-time') filtered = products.filter(p => p.tiers.some(t => t.period && t.period.includes('one-time')));
  else if (category === 'enterprise') filtered = products.filter(p => p.tiers.some(t => t.price === null));
  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.tagline.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q)
    );
  }
  return filtered;
};

const sortProducts = (products, sortBy) => {
  if (sortBy === 'price-asc') return [...products].sort((a, b) => (a.tiers[0].price || 0) - (b.tiers[0].price || 0));
  if (sortBy === 'price-desc') return [...products].sort((a, b) => (b.tiers[0].price || Infinity) - (a.tiers[0].price || Infinity));
  return products;
};

const ShopPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const filtered = sortProducts(filterProducts(SHOP_PRODUCTS, category, search), sortBy);

  const addToCart = (product, tier) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.tierId === tier.id);
      if (existing) {
        return prev.map((i) => i.tierId === tier.id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { tierId: tier.id, productId: product.id, productName: product.name, tierLabel: tier.label, price: tier.price, period: tier.period, quantity: 1 }];
    });
    setCartOpen(true);
  };

  const removeFromCart = (tierId) => {
    setCart((prev) => prev.filter((i) => i.tierId !== tierId));
  };

  const updateQuantity = (tierId, delta) => {
    setCart((prev) =>
      prev
        .map((i) => i.tierId === tierId ? { ...i, quantity: i.quantity + delta } : i)
        .filter((i) => i.quantity > 0)
    );
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const response = await fetch(`${API_URL}/auth/firebase-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        credentials: 'include',
      });
      if (response.ok) {
        const userData = await response.json();
        login(userData);
      }
    } catch (error) {
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-browser' || error.code === 'auth/cancelled-popup-request') {
        await signInWithRedirect(auth, googleProvider);
      }
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      handleGoogleLogin();
      return;
    }
    if (cart.length === 0) return;
    setCheckoutLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { alert('Failed to load payment gateway. Please try again.'); return; }

      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_URL}/shop/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ items: cart }),
      });

      if (!res.ok) throw new Error('Failed to create order');
      const order = await res.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: 'INR',
        order_id: order.id,
        name: 'ioteverythin',
        description: `${cart.length} item${cart.length > 1 ? 's' : ''}`,
        prefill: { email: user.email, name: user.name },
        theme: { color: '#a89060' },
        handler: async (response) => {
          try {
            await fetch(`${API_URL}/shop/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
              body: JSON.stringify(response),
            });
            setCart([]);
            setCartOpen(false);
            alert('Payment successful! Your licenses have been activated.');
          } catch { alert('Payment verification failed. Contact support.'); }
        },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div style={{ background: '#0a0a0a', minHeight: '100vh', color: '#e5e5e5' }}>
      {/* ===== SHOP HEADER ===== */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 40,
          background: 'rgba(10,10,10,0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #1a1a1a',
        }}
      >
        {/* Top bar */}
        <div style={{ background: '#111', borderBottom: '1px solid #1a1a1a' }}>
          <div className="flex items-center justify-between px-4 lg:px-8" style={{ height: 36 }}>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-xs text-[#888] hover:text-[#ccc] transition-colors"
            >
              <Home size={12} />
              <span>Back to ioteverythin</span>
            </button>
            <div className="flex items-center gap-4 text-xs text-[#888]">
              <span>Free shipping on digital products</span>
            </div>
          </div>
        </div>

        {/* Main header */}
        <div className="flex items-center gap-4 px-4 lg:px-8" style={{ height: 64 }}>
          {/* Mobile sidebar toggle */}
          <button
            className="lg:hidden p-2 -ml-2 text-[#888] hover:text-white transition-colors"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <Menu size={20} />
          </button>

          {/* Logo */}
          <Link to="/shop" className="flex items-center gap-3 flex-shrink-0">
            <img src="/logo_2.png" alt="ioteverythin" style={{ width: 28, height: 28 }} />
            <div className="flex items-baseline gap-1.5">
              <span className="text-white font-medium text-lg tracking-tight">ioteverythin</span>
              <span className="text-[#a89060] font-light text-lg">shop</span>
            </div>
          </Link>

          {/* Search bar */}
          <div className="flex-1 max-w-2xl mx-4 hidden sm:block">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products, tools, and services..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm text-white placeholder-[#555] outline-none transition-colors"
                style={{
                  background: '#151515',
                  border: '1px solid #222',
                }}
                onFocus={(e) => e.target.style.borderColor = '#a89060'}
                onBlur={(e) => e.target.style.borderColor = '#222'}
              />
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-3 ml-auto flex-shrink-0">
            {user ? (
              <div className="flex items-center gap-2.5">
                {user.photoURL ? (
                  <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#1a1a1a' }}>
                    <User size={14} className="text-[#888]" />
                  </div>
                )}
                <span className="text-sm text-[#999] hidden md:block">{user.displayName || user.name || user.email?.split('@')[0]}</span>
              </div>
            ) : (
              <button
                onClick={handleGoogleLogin}
                className="text-xs font-medium px-4 py-2 rounded-lg transition-colors"
                style={{ background: '#1a1a1a', color: '#ccc', border: '1px solid #2a2a2a' }}
              >
                Sign In
              </button>
            )}
            <button
              onClick={() => setCartOpen(true)}
              className="relative p-2.5 rounded-lg transition-colors hover:bg-[#1a1a1a]"
              style={{ color: '#999' }}
            >
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full flex items-center justify-center text-[10px] font-bold px-1"
                  style={{ background: '#a89060', color: '#000' }}
                >
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="sm:hidden px-4 pb-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm text-white placeholder-[#555] outline-none"
              style={{ background: '#151515', border: '1px solid #222' }}
            />
          </div>
        </div>
      </header>

      {/* ===== BODY: SIDEBAR + CONTENT ===== */}
      <div className="flex">
        {/* Sidebar backdrop (mobile) */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/60 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-[101px] lg:top-[101px] left-0 z-30
            w-[260px] h-[calc(100vh-101px)] overflow-y-auto flex-shrink-0
            transition-transform duration-200
            lg:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
          style={{ background: '#0e0e0e', borderRight: '1px solid #1a1a1a' }}
        >
          <div className="p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#666] mb-4 flex items-center gap-2">
              <SlidersHorizontal size={13} />
              Categories
            </h3>
            <nav className="space-y-1">
              {SHOP_CATEGORIES.map((cat) => {
                const isActive = category === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => { setCategory(cat.id); setSidebarOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 text-left"
                    style={{
                      background: isActive ? 'rgba(168,144,96,0.1)' : 'transparent',
                      color: isActive ? '#d4b96a' : '#888',
                      fontWeight: isActive ? 500 : 400,
                    }}
                  >
                    <cat.icon size={16} style={{ opacity: isActive ? 1 : 0.5 }} />
                    {cat.label}
                    {isActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ background: '#a89060' }} />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Sidebar info */}
          <div className="mx-5 mt-4 p-4 rounded-xl" style={{ background: '#111', border: '1px solid #1a1a1a' }}>
            <p className="text-xs text-[#666] leading-relaxed">
              All digital products include instant delivery and free updates for the duration of your plan.
            </p>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 px-4 lg:px-8 py-6">
          {/* Toolbar: results count + sort */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-sm text-[#888]">
                {filtered.length === SHOP_PRODUCTS.length
                  ? `Showing all ${filtered.length} products`
                  : `${filtered.length} of ${SHOP_PRODUCTS.length} products`
                }
              </span>
              {category !== 'all' && (
                <span className="ml-2 text-xs px-2 py-1 rounded-md" style={{ background: 'rgba(168,144,96,0.12)', color: '#a89060' }}>
                  {SHOP_CATEGORIES.find(c => c.id === category)?.label}
                </span>
              )}
              {search && (
                <span className="ml-2 text-xs text-[#666]">
                  for "{search}"
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#666] hidden sm:block">Sort by</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-xs rounded-lg px-3 py-2 outline-none cursor-pointer"
                style={{ background: '#151515', color: '#999', border: '1px solid #222' }}
              >
                <option value="featured">Featured</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          </div>

          {/* Product grid */}
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((product) => (
                <ShopProductCard key={product.id} product={product} onAddToCart={addToCart} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-[#555]">
              <Search size={48} className="mb-4" />
              <p className="text-lg mb-2">No products found</p>
              <p className="text-sm">Try a different search term or category.</p>
              <button
                onClick={() => { setSearch(''); setCategory('all'); }}
                className="mt-4 text-xs px-4 py-2 rounded-lg transition-colors"
                style={{ background: '#1a1a1a', color: '#999', border: '1px solid #2a2a2a' }}
              >
                Clear filters
              </button>
            </div>
          )}
        </main>
      </div>

      {/* ===== CART DRAWER ===== */}
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 transition-opacity duration-300"
        style={{
          background: 'rgba(0, 0, 0, 0.6)',
          opacity: cartOpen ? 1 : 0,
          pointerEvents: cartOpen ? 'auto' : 'none',
        }}
        onClick={() => setCartOpen(false)}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-md z-50 flex flex-col transition-transform duration-300"
        style={{
          background: '#0e0e0e',
          borderLeft: '1px solid #1a1a1a',
          transform: cartOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Cart Header */}
        <div className="flex items-center justify-between p-6" style={{ borderBottom: '1px solid #1a1a1a' }}>
          <h2 className="text-lg font-medium text-white">Cart ({cartCount})</h2>
          <button onClick={() => setCartOpen(false)} className="text-[#888] hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-[#555]">
              <ShoppingCart size={48} className="mb-4" />
              <p className="text-sm">Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div
                  key={item.tierId}
                  className="rounded-xl p-4"
                  style={{ background: '#151515', border: '1px solid #1a1a1a' }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-white text-sm font-medium">{item.productName}</p>
                      <p className="text-[#888] text-xs">{item.tierLabel}{item.period || ''}</p>
                    </div>
                    <button onClick={() => removeFromCart(item.tierId)} className="text-[#555] hover:text-[#fb565b] transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.tierId, -1)}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-[#888] hover:text-white transition-colors"
                        style={{ border: '1px solid #2a2a2a' }}
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-white text-sm w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.tierId, 1)}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-[#888] hover:text-white transition-colors"
                        style={{ border: '1px solid #2a2a2a' }}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <span className="text-white text-sm font-medium">{formatPrice(item.price * item.quantity)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Footer */}
        <div className="p-6" style={{ borderTop: '1px solid #1a1a1a' }}>
          <div className="flex items-center justify-between mb-4">
            <span className="text-[#888] text-sm">Total</span>
            <span className="text-white text-xl font-light">{formatPrice(cartTotal)}</span>
          </div>
          <button
            onClick={handleCheckout}
            disabled={cart.length === 0 || checkoutLoading}
            className="w-full py-3.5 rounded-lg text-xs font-medium uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2"
            style={{
              background: cart.length === 0 ? '#2a2a2a' : '#a89060',
              color: cart.length === 0 ? '#555' : '#000',
              cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {checkoutLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Processing...
              </>
            ) : !user ? (
              'Sign In to Checkout'
            ) : (
              'Checkout'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const LoadingIntro = () => {
  // Single-orb approach: logo blurs away while overlay fades, revealing the
  // FixedWheel orb growing behind it. No mini-orb = no two-orb problem.
  // Only plays once per session — skip on subsequent visits.
  const alreadySeen = sessionStorage.getItem('intro-seen');
  const [phase, setPhase] = useState(alreadySeen ? 'done' : 'logo');
  const [visible, setVisible] = useState(!alreadySeen);

  const anim = useRef({
    orbIntro: -0.7,
    orbIntroTarget: -0.7,
    morphT: 0,
    overlayOpacity: 1,
    logoBlur: 0,
    logoOpacity: 1,
    ringsOpacity: 1,
  });

  const containerEl = useRef(null);
  const logoEl = useRef(null);
  const ringsEl = useRef(null);

  // logo   (0–2.4s)  — logo pulses with seismic rings
  // morph  (2.4–4.0s) — logo blurs heavily + fades, overlay fades, real orb grows
  // zoom   (4.0–5.4s) — orb zooms out big
  // settle (5.4–6.6s) — orb shrinks to normal, content fades in
  useEffect(() => {
    if (alreadySeen) {
      // Skip intro — immediately set CSS vars to final state
      const root = document.documentElement;
      root.style.setProperty('--orb-intro', '0');
      root.style.setProperty('--orb-z', '20');
      root.style.setProperty('--content-hide', '0');
      return;
    }
    const t1 = setTimeout(() => setPhase('morph'), 2400);
    const t2 = setTimeout(() => setPhase('zoom'), 4000);
    const t3 = setTimeout(() => setPhase('settle'), 5400);
    const t4 = setTimeout(() => { setPhase('done'); sessionStorage.setItem('intro-seen', '1'); }, 6600);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [alreadySeen]);

  useEffect(() => {
    const root = document.documentElement;
    if (phase === 'logo') {
      anim.current.orbIntroTarget = -0.7;
      root.style.setProperty('--orb-z', '110');
      root.style.setProperty('--content-hide', '1');
    } else if (phase === 'morph') {
      // Start growing the real orb from tiny → visible while overlay fades
      anim.current.orbIntroTarget = 0.2;
      root.style.setProperty('--orb-z', '110');
      root.style.setProperty('--content-hide', '1');
    } else if (phase === 'zoom') {
      // Orb zooms out big
      anim.current.orbIntroTarget = 1.2;
      root.style.setProperty('--orb-z', '20');
      root.style.setProperty('--content-hide', '1');
    } else if (phase === 'settle') {
      anim.current.orbIntroTarget = 0;
      root.style.setProperty('--orb-z', '20');
      root.style.setProperty('--content-hide', '0');
    } else if (phase === 'done') {
      anim.current.orbIntroTarget = 0;
      root.style.setProperty('--orb-z', '20');
      root.style.setProperty('--content-hide', '0');
      setVisible(false);
    }
  }, [phase]);

  useEffect(() => {
    const root = document.documentElement;
    let rafId;
    const lerp = (a, b, t) => a + (b - a) * t;

    const tick = () => {
      const a = anim.current;
      const isMorph = phase === 'morph';
      const isZoom = phase === 'zoom';
      const isSettle = phase === 'settle';
      const isDone = phase === 'done';

      // --- Real orb --orb-intro ---
      const introSpeed = isMorph ? 0.03 : isZoom ? 0.04 : isSettle ? 0.06 : 0.05;
      a.orbIntro = lerp(a.orbIntro, a.orbIntroTarget, introSpeed);
      if (Math.abs(a.orbIntro - a.orbIntroTarget) < 0.001) a.orbIntro = a.orbIntroTarget;
      root.style.setProperty('--orb-intro', a.orbIntro.toFixed(4));

      // --- Logo blur + fade during morph ---
      if (isMorph) {
        a.logoBlur = lerp(a.logoBlur, 40, 0.04);
        a.logoOpacity = lerp(a.logoOpacity, 0, 0.05);
      } else if (isZoom || isSettle || isDone) {
        a.logoBlur = 40;
        a.logoOpacity = 0;
      }

      // --- Overlay fades during morph (logo dissolves into the growing orb behind) ---
      if (isMorph) {
        a.overlayOpacity = lerp(a.overlayOpacity, 0, 0.03);
      } else if (isZoom || isSettle || isDone) {
        a.overlayOpacity = lerp(a.overlayOpacity, 0, 0.15);
      }

      if (logoEl.current) {
        logoEl.current.style.filter = a.logoBlur > 0.5
          ? `blur(${a.logoBlur.toFixed(1)}px) drop-shadow(0 0 20px rgba(0,180,255,0.6))`
          : (phase === 'logo' ? '' : 'none');
        logoEl.current.style.opacity = a.logoOpacity.toFixed(3);
      }

      if (containerEl.current) {
        containerEl.current.style.opacity = a.overlayOpacity.toFixed(3);
        if (a.overlayOpacity < 0.01) containerEl.current.style.pointerEvents = 'none';
      }

      // --- Rings fade during morph ---
      const ringsTarget = isMorph || isZoom || isSettle || isDone ? 0 : 1;
      a.ringsOpacity = lerp(a.ringsOpacity, ringsTarget, 0.06);
      if (ringsEl.current) ringsEl.current.style.opacity = a.ringsOpacity.toFixed(3);

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [phase]);

  if (!visible) return null;

  return (
    <div
      ref={containerEl}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 120,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes logoPulse {
          0%   { transform: scale(1);    filter: drop-shadow(0 0 12px rgba(255,165,0,0.3)); }
          50%  { transform: scale(1.06); filter: drop-shadow(0 0 30px rgba(255,165,0,0.7)) drop-shadow(0 0 60px rgba(255,120,0,0.3)); }
          100% { transform: scale(1);    filter: drop-shadow(0 0 12px rgba(255,165,0,0.3)); }
        }
        @keyframes seismicWave {
          0%   { transform: translate(-50%, -50%) scale(0.8); opacity: 0.5; }
          100% { transform: translate(-50%, -50%) scale(3.0); opacity: 0; }
        }
      `}</style>

      {/* Logo + rings centered */}
      <div style={{ position: 'relative', width: 160, height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Seismic wave rings — centered on logo */}
        <div ref={ringsEl} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: 120,
                height: 120,
                top: '50%',
                left: '50%',
                borderRadius: '50%',
                border: '2px solid rgba(255,165,0,0.3)',
                animation: `seismicWave 2.8s ease-out ${i * 0.9}s infinite backwards`,
              }}
            />
          ))}
        </div>

        {/* PNG logo — blurs + fades during morph, revealing the real orb behind */}
        <img
          ref={logoEl}
          src="/logo_2.png"
          alt="Loading"
          style={{
            width: 120,
            height: 120,
            objectFit: 'contain',
            animation: phase === 'logo' ? 'logoPulse 2s ease-in-out infinite' : 'none',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
};

// Layout wrapper
const Layout = ({ children }) => {
  const location = useLocation();
  useOrbFocusScroll();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  return (
    <>
      <LoadingIntro />
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.3 }}>
        <DotGrid
          dotSize={4}
          gap={28}
          baseColor="#3d2e00"
          activeColor="#ffd700"
          proximity={140}
          shockRadius={260}
          shockStrength={6}
        />
      </div>
      <GlobalParticles />
      <OrbBackdrop />
      <div
        style={{
          opacity: 'calc(1 - var(--content-hide, 1))',
          transition: 'opacity 0.9s ease',
        }}
      >
        <Navigation />
        <main className="relative z-10">{children}</main>
        <Footer />
      </div>
      <FixedWheel />
    </>
  );
};

// App Component
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

// Splits /shop (standalone) from everything else (Layout-wrapped)
const AppShell = () => {
  const location = useLocation();
  if (location.pathname === '/shop') return <ShopPage />;
  return (
    <Layout>
      <AppRouter />
    </Layout>
  );
};

export default App;
